import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AppHeader } from '../../components/AppHeader/AppHeader.js';
import { SectionHeader } from '../../components/SectionHeader/SectionHeader.js';
import { FolderHeader } from '../../components/FolderHeader/FolderHeader.js';
import { CardColor } from '../../components/CardColor/CardColor.js';
import { Button } from '../../components/Button/Button.js';
import { Icon } from '../../components/Icon/Icon.js';
import { MoveModal } from '../../components/MoveModal/MoveModal.js';
import { ConfirmationModal } from '../../components/ConfirmationModal/ConfirmationModal.js';
import { StartScreenModal } from '../../components/StartScreenModal/StartScreenModal.js';
import { SEOProtection } from '../../components/SEOProtection/SEOProtection.js';
import { FigmaPromoCard } from '../../components/FigmaPromoCard/FigmaPromoCard.js';
import { parseColor, formatOklch } from '../../utils/colorUtils.js';

import { createColorEngine } from '../../utils/colorEngine.js';
import { measureContrastPair, roundContrastLevel, colorInputForMeasurement } from '../../utils/wcagContrast.js';
import { updateFavicon, getHexFromCSSVar } from '../../utils/faviconUtils';
import { exportForFigmaPlugin, importFromFigmaPlugin, downloadJsonFile, readJsonFile, getColorsInVisualOrder } from '../../utils/figmaSyncUtils';
import { hydrateImportedData, formatJsonImportError } from '../../utils/hydrateImportedColors.js';

import './Main.css';

const CONFIG_STORAGE_KEY = 'betterlch-editor-config';
const FIGMA_PROMO_DISMISSED_KEY = 'betterlch-figma-promo-dismissed';
const FIGMA_PROMO_DELAY_MS = 2000;
const SAVE_DEBOUNCE_MS = 1500;
// Use sessionStorage for per-tab config persistence
const getStorage = () => sessionStorage;

function contrastEngineOptions(algorithm, colorSpace = 'p3') {
  return {
    contrastAlgorithm: algorithm === 'wcag' ? 'wcag' : 'apca',
    colorSpace: colorSpace === 'p3' ? 'p3' : 'srgb'
  };
}

/** Normalize free-form input to one of: 'srgb' | 'p3'. */
function normalizeColorSpace(value) {
  if (value === undefined || value === null || value === '') return 'p3';
  return value === 'p3' || value === 'display-p3' || value === 'displayP3' ? 'p3' : 'srgb';
}

function formatExportTimestamp() {
  const now = new Date();
  const year = String(now.getFullYear()).padStart(4, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}.${minutes}.${seconds}`;
}

/** Build v3 export JSON including P3 channel payloads for the Figma plugin. */
function buildFigmaExportPayload({
  primitives,
  primitiveFolders,
  tokens,
  tokenFolders,
  colorThemes,
  primitivesSectionTitle,
  themesSectionTitle,
  contrastAlgorithm,
  colorSpace,
}) {
  const allPrimitives = primitives || [];
  const engineOpts = contrastEngineOptions(contrastAlgorithm, colorSpace);
  const primitivesEngine = createColorEngine(allPrimitives, engineOpts);
  const computedPrimitives = primitivesEngine.computeAll();
  const computedTokensByTheme = {};
  for (const theme of colorThemes || []) {
    const themeTokens = (tokens && tokens[theme.id]) || [];
    const allForTheme = [
      ...allPrimitives.map((p) => ({ ...p, isToken: false })),
      ...themeTokens.map((t) => ({ ...t, isToken: true })),
    ];
    const tokensEngine = createColorEngine(allForTheme, engineOpts);
    computedTokensByTheme[theme.id] = tokensEngine.computeAll();
  }
  return exportForFigmaPlugin(
    {
      primitives,
      primitiveFolders,
      tokens,
      tokenFolders,
      colorThemes,
      primitivesSectionTitle,
      themesSectionTitle,
      contrastAlgorithm,
      colorSpace,
    },
    computedPrimitives,
    computedTokensByTheme
  );
}

function inferContrastAlgorithmFromCollections(collections = []) {
  const all = Array.isArray(collections) ? collections : [];
  const hasWcagKeyword = all.some((c) => {
    const name = String(c?.name || '').toLowerCase();
    const id = String(c?.id || '').toLowerCase();
    return name.includes('wcag') || id.includes('wcag');
  });
  if (hasWcagKeyword) return 'wcag';

  const contrastLevels = [];
  for (const collection of all) {
    const modes = Array.isArray(collection?.modes) ? collection.modes : [];
    for (const mode of modes) {
      const colors = Array.isArray(mode?.colors) ? mode.colors : [];
      for (const color of colors) {
        if (color?.mode === 'contrast' && typeof color?.contrastLevel === 'number' && isFinite(color.contrastLevel)) {
          contrastLevels.push(color.contrastLevel);
        }
      }
    }
  }
  if (contrastLevels.length === 0) return 'apca';
  const max = Math.max(...contrastLevels);
  // WCAG ratio is 1..21. APCA can exceed this in normal use.
  if (max <= 21) return 'wcag';
  return 'apca';
}

/**
 * Prefer explicit settings / data fields; only heuristically infer when both are absent.
 * Low APCA Lc targets (e.g. 8, 15, 30) look like WCAG ratios, so inference must not
 * override a declared `"apca"` setting.
 */
function resolveContrastAlgorithm(config, data) {
  const fromSettings = config?.settings?.contrastAlgorithm;
  if (fromSettings === 'wcag' || fromSettings === 'apca') return fromSettings;
  const fromData = data?.contrastAlgorithm;
  if (fromData === 'wcag' || fromData === 'apca') return fromData;
  if (Array.isArray(data?.collections)) return inferContrastAlgorithmFromCollections(data.collections);
  return 'apca';
}

/** Normalize config to a single theme (first theme only). Used when singleTheme is true. */
const normalizeToSingleThemeData = (data) => {
  const themes = Array.isArray(data.colorThemes) && data.colorThemes.length > 0
    ? data.colorThemes
    : [{ id: 'default', name: 'Default' }];
  const first = themes[0];
  const tokens = data.tokens && typeof data.tokens === 'object' ? data.tokens : {};
  const tokenFolders = data.tokenFolders && typeof data.tokenFolders === 'object' ? data.tokenFolders : {};
  return {
    ...data,
    colorThemes: [first],
    tokens: { [first.id]: tokens[first.id] || [] },
    tokenFolders: { [first.id]: tokenFolders[first.id] || [] },
    selectedTokenTheme: first.name
  };
};

export const Main = ({ singleTheme = false } = {}) => {
  const [selectedTokenTheme, setSelectedTokenTheme] = useState('Default');

  // Section titles (editable)
  const [primitivesSectionTitle, setPrimitivesSectionTitle] = useState('Primitives');
  const [themesSectionTitle, setThemesSectionTitle] = useState('Semantic');
  
  // Primitives state (same across all themes)
  const [primitives, setPrimitives] = useState([]);
  const [primitiveFolders, setPrimitiveFolders] = useState([]);
  
  // Multi-theme system
  const [colorThemes, setColorThemes] = useState([
    { id: 'default', name: 'Default' }
  ]);
  
  // Keep a ref to always have the latest colorThemes (avoids stale closures)
  const colorThemesRef = useRef(colorThemes);
  useEffect(() => {
    console.log('[useEffect] Updating colorThemesRef from:', colorThemesRef.current.map(t => t.name), 'to:', colorThemes.map(t => t.name));
    colorThemesRef.current = colorThemes;
  }, [colorThemes]);

  // Refs for persistence: latest state (for beforeunload save) and debounce timer
  const saveTimeoutRef = useRef(null);
  const stateForSaveRef = useRef({
    primitives: [],
    primitiveFolders: [],
    colorThemes: [{ id: 'default', name: 'Default' }],
    tokens: {},
    tokenFolders: {},
    selectedTokenTheme: 'Default',
    primitivesSectionTitle: 'Primitives',
    themesSectionTitle: 'Semantic',
    contrastAlgorithm: 'apca',
    colorSpace: 'srgb'
  });
  const isRestoreAttemptedRef = useRef(false);
  
  // Theme dropdown state moved to SectionHeader to avoid full-page re-render on open
  const themeManageButtonRef = useRef(null);
  
  // Primitive folder move modal state
  const [showFolderMoveModal, setShowFolderMoveModal] = useState(false);
  const [folderToMove, setFolderToMove] = useState(null);

  // Token folder move modal state
  const [showTokenFolderMoveModal, setShowTokenFolderMoveModal] = useState(false);
  const [tokenFolderToMove, setTokenFolderToMove] = useState(null);

  // Confirmation modal state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'warning',
    onConfirm: null
  });

  // Unsaved changes confirmation state
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [unsavedChangesModalVariant, setUnsavedChangesModalVariant] = useState(null); // 'import' | 'replace' (start from scratch / preset)
  const [pendingAction, setPendingAction] = useState(null); // Function to execute after confirmation
  const isModalOpenRef = useRef(false); // Track if modal is open to prevent immediate execution
  const [showStartScreenModal, setShowStartScreenModal] = useState(false); // Modal for start screen cards
  
  // Themes state - now per theme
  const [tokens, setTokens] = useState({});
  const [tokenFolders, setTokenFolders] = useState({});

  // Start screen: show choices until user picks "Start from scratch" or loads data
  const [hasChosenStartScreen, setHasChosenStartScreen] = useState(false);

  /** Global contrast metric for contrast-based colors: APCA (Lc) or WCAG 2.x contrast ratio */
  const [contrastAlgorithm, setContrastAlgorithm] = useState('apca');
  /**
   * Workspace color space: 'srgb' or 'p3'.
   * - Affects APCA measurement (apcach `'srgb'` vs `'p3'` variant).
   * - WCAG always uses sRGB per W3C spec, so this toggle is a no-op for WCAG.
   * - 'p3' should match Polychrom-on-P3-document readings; 'srgb' matches Polychrom-on-sRGB.
   */
  const [colorSpace, setColorSpace] = useState('p3');
  const contrastEngineOpts = useMemo(
    () => contrastEngineOptions(contrastAlgorithm, colorSpace),
    [contrastAlgorithm, colorSpace]
  );

  // Figma promo card state
  const [showFigmaPromo, setShowFigmaPromo] = useState(false);
  const [activePresetType, setActivePresetType] = useState(null); // 'graphic' or 'ui'
  const figmaPromoTimerRef = useRef(null);

  // Confirmation modal helpers
  const showConfirmation = (config) => {
    setConfirmationConfig(config);
    setShowConfirmationModal(true);
  };

  const handleConfirmationModalClose = () => {
    setShowConfirmationModal(false);
    setConfirmationConfig({
      title: '',
      message: '',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      type: 'warning',
      onConfirm: null
    });
  };

  const handleConfirmationModalConfirm = () => {
    if (confirmationConfig.onConfirm) {
      confirmationConfig.onConfirm();
    }
  };

  const clearConfigStorage = () => {
    try {
      getStorage().removeItem(CONFIG_STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear config storage', e);
    }
  };

  const hasSavedConfig = () => {
    try {
      const raw = getStorage().getItem(CONFIG_STORAGE_KEY);
      if (!raw || raw.trim() === '') return false;
      const data = JSON.parse(raw);
      if (!Array.isArray(data.primitives) || !Array.isArray(data.primitiveFolders)) return false;
      if (!Array.isArray(data.colorThemes) || data.colorThemes.length === 0) return false;
      if (typeof data.tokens !== 'object' || data.tokens === null) return false;
      if (typeof data.tokenFolders !== 'object' || data.tokenFolders === null) return false;
      const hasPrimitives = (data.primitives || []).length > 0;
      const hasTokens = (data.colorThemes || []).some(t => {
        const list = data.tokens?.[t.id];
        return Array.isArray(list) && list.length > 0;
      });
      return hasPrimitives || hasTokens;
    } catch (e) {
      return false;
    }
  };

  const getSavedConfigInfo = () => {
    try {
      const raw = getStorage().getItem(CONFIG_STORAGE_KEY);
      if (!raw || raw.trim() === '') return null;
      const data = JSON.parse(raw);
      const primitivesCount = (data.primitives || []).length;
      const tokensCount = (data.colorThemes || []).reduce((sum, t) => {
        const list = data.tokens?.[t.id];
        return sum + (Array.isArray(list) ? list.length : 0);
      }, 0);
      return {
        primitivesSectionTitle: data.primitivesSectionTitle || 'Primitives',
        themesSectionTitle: data.themesSectionTitle || 'Semantic',
        primitivesCount,
        tokensCount
      };
    } catch (e) {
      return null;
    }
  };

  // Figma promo card helpers
  const isFigmaPromoDismissed = (presetType) => {
    try {
      const dismissed = JSON.parse(localStorage.getItem(FIGMA_PROMO_DISMISSED_KEY) || '{}');
      return !!dismissed[presetType];
    } catch {
      return false;
    }
  };

  const dismissFigmaPromo = (presetType) => {
    try {
      const dismissed = JSON.parse(localStorage.getItem(FIGMA_PROMO_DISMISSED_KEY) || '{}');
      dismissed[presetType] = true;
      localStorage.setItem(FIGMA_PROMO_DISMISSED_KEY, JSON.stringify(dismissed));
    } catch (e) {
      console.warn('Failed to save figma promo dismissed state', e);
    }
  };

  const scheduleFigmaPromo = (presetType) => {
    // Clear any existing timer
    if (figmaPromoTimerRef.current) {
      clearTimeout(figmaPromoTimerRef.current);
    }
    // Don't show if user previously dismissed for this preset
    if (isFigmaPromoDismissed(presetType)) return;
    setActivePresetType(presetType);
    figmaPromoTimerRef.current = setTimeout(() => {
      setShowFigmaPromo(true);
      figmaPromoTimerRef.current = null;
    }, FIGMA_PROMO_DELAY_MS);
  };

  const handleFigmaPromoClose = () => {
    setShowFigmaPromo(false);
  };

  const handleFigmaPromoDontShowAgain = () => {
    if (activePresetType) {
      dismissFigmaPromo(activePresetType);
    }
    setShowFigmaPromo(false);
  };

  const restoreSavedConfig = () => {
    try {
      const raw = getStorage().getItem(CONFIG_STORAGE_KEY);
      if (!raw || raw.trim() === '') return false;
      const data = JSON.parse(raw);
      if (!Array.isArray(data.primitives) || !Array.isArray(data.primitiveFolders)) return false;
      if (!Array.isArray(data.colorThemes) || data.colorThemes.length === 0) return false;
      if (typeof data.tokens !== 'object' || data.tokens === null) return false;
      if (typeof data.tokenFolders !== 'object' || data.tokenFolders === null) return false;
      const dataToApply = singleTheme ? normalizeToSingleThemeData(data) : data;
      isBulkLoadRef.current = true;
      shouldExpandFoldersRef.current = true;
      setPrimitives(dataToApply.primitives);
      setPrimitiveFolders(dataToApply.primitiveFolders || []);
      const newThemes = dataToApply.colorThemes;
      setColorThemes(newThemes);
      colorThemesRef.current = newThemes;
      setTokens(dataToApply.tokens);
      setTokenFolders(dataToApply.tokenFolders);
      const selected = dataToApply.selectedTokenTheme && newThemes.some(t => t.name === dataToApply.selectedTokenTheme)
        ? dataToApply.selectedTokenTheme
        : (newThemes[0]?.name || 'Default');
      setSelectedTokenTheme(selected);
      setPrimitivesSectionTitle(data.primitivesSectionTitle ?? 'Primitives');
      setThemesSectionTitle(data.themesSectionTitle ?? 'Semantic');
      setContrastAlgorithm(data.contrastAlgorithm === 'wcag' ? 'wcag' : 'apca');
      setColorSpace(normalizeColorSpace(data.colorSpace));
      setHasChosenStartScreen(true);
      return true;
    } catch (e) {
      console.warn('Failed to restore config from storage', e);
      return false;
    }
  };

  const handleLogoClick = () => {
    // Reload/refresh the current config from sessionStorage
    restoreSavedConfig();
  };

  const handleStartFromScratch = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    // Always check for saved config first - MUST check sessionStorage synchronously
    const hasConfig = hasSavedConfig();
    
    if (hasConfig) {
      // Show modal first, action will execute ONLY after confirmation
      const actionToExecute = () => {
        clearConfigStorage();
        setPrimitives([]);
        setPrimitiveFolders([]);
        setColorThemes([{ id: 'default', name: 'Default' }]);
        colorThemesRef.current = [{ id: 'default', name: 'Default' }];
        setTokens({});
        setTokenFolders({});
        setSelectedTokenTheme('Default');
        setPrimitivesSectionTitle('Primitives');
        setThemesSectionTitle('Semantic');
        setContrastAlgorithm('apca');
        setColorSpace('p3');
        setPrimitivePrecomputed(new Map());
        setTokenPrecomputed(new Map());
        setHasChosenStartScreen(true);
        setShowStartScreenModal(false); // Close start screen modal
      };
      
      setUnsavedChangesModalVariant('replace');
      setPendingAction(() => actionToExecute);
      setShowUnsavedChangesModal(true);
      setShowStartScreenModal(false); // Close start screen modal
      
      // CRITICAL: Return early - do NOT execute any action here
      // The action will ONLY execute when user clicks "Export and continue" or "Continue anyway"
      return;
    }
    
    // Only proceed if no saved config exists
    clearConfigStorage();
    setPrimitives([]);
    setPrimitiveFolders([]);
    setColorThemes([{ id: 'default', name: 'Default' }]);
    colorThemesRef.current = [{ id: 'default', name: 'Default' }];
    setTokens({});
    setTokenFolders({});
    setSelectedTokenTheme('Default');
    setPrimitivesSectionTitle('Primitives');
    setThemesSectionTitle('Semantic');
    setContrastAlgorithm('apca');
    setColorSpace('p3');
    setPrimitivePrecomputed(new Map());
    setTokenPrecomputed(new Map());
    setHasChosenStartScreen(true);
    setShowStartScreenModal(false); // Close start screen modal
  };


  const handleUnsavedChangesExportAndContinue = async () => {
    // Get the action BEFORE closing modal to ensure we have it
    const action = pendingAction;
    
    // Clear pending action immediately to prevent double execution
    setPendingAction(null);
    
    // Mark modal as closed so action can execute
    isModalOpenRef.current = false;
    
    // Close modal
    setShowUnsavedChangesModal(false);
    
    // Wait for React to process the state update and close the modal
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Export from saved config (we're on start screen when this modal shows for import/preset actions)
    // Use exportFromSavedConfig which reads from localStorage
    await exportFromSavedConfig();
    
    // Small delay to ensure download is triggered before proceeding
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Execute action ONLY after export completes
    if (action && typeof action === 'function') {
      const result = action();
      if (result && typeof result.then === 'function') {
        await result;
      }
    }
  };

  const handleUnsavedChangesContinueAnyway = async () => {
    // Get the action BEFORE closing modal to ensure we have it
    const action = pendingAction;
    
    // Clear pending action immediately to prevent double execution
    setPendingAction(null);
    
    // Close modal first
    setShowUnsavedChangesModal(false);
    
    // Wait for React to process the state update and close the modal
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Execute action ONLY after modal is closed
    if (action && typeof action === 'function') {
      const result = action();
      if (result && typeof result.then === 'function') {
        await result;
      }
    }
  };

  // Helper functions - defined before useMemo to avoid hoisting issues
  const getCurrentTheme = () => {
    return colorThemes.find(t => t.name === selectedTokenTheme) || colorThemes[0];
  };

  const getCurrentThemeId = () => {
    return getCurrentTheme()?.id || 'default';
  };

  // True when every theme has no tokens and no token folders
  const allThemesEmpty = useMemo(() => {
    return colorThemes.every(
      (t) =>
        (tokens[t.id] || []).length === 0 &&
        (tokenFolders[t.id] || []).length === 0
    );
  }, [colorThemes, tokens, tokenFolders]);

  const showStartScreen =
    primitives.length === 0 && allThemesEmpty && !hasChosenStartScreen;
  
  // Memoize color engines to prevent unnecessary recreations
  const primitiveEngine = useMemo(() => {
    return primitives.length > 0 ? createColorEngine(primitives, contrastEngineOpts) : null;
  }, [primitives, contrastEngineOpts]);

  const tokenEngine = useMemo(() => {
    const currentThemeId = getCurrentThemeId();
    const currentTokens = tokens[currentThemeId] || [];
    const allForTheme = [...(primitives || []).map(p => ({ ...p, isToken: false })), ...(currentTokens || []).map(t => ({ ...t, isToken: true }))];
    return allForTheme.length > 0 ? createColorEngine(allForTheme, contrastEngineOpts) : null;
  }, [primitives, tokens, selectedTokenTheme, contrastEngineOpts]);

  // Incremental precomputed color maps (single source of truth)
  const [primitivePrecomputed, setPrimitivePrecomputed] = useState(new Map());
  const [tokenPrecomputed, setTokenPrecomputed] = useState(new Map());
  const isBulkLoadRef = useRef(false);
  const shouldExpandFoldersRef = useRef(false);

  // Helper to merge patches into existing maps
  const mergeMaps = (baseMap, patchMap) => {
    const next = new Map(baseMap);
    for (const [k, v] of (patchMap || new Map()).entries()) {
      next.set(k, v);
    }
    return next;
  };

  // Rebuild token precomputed map when theme or workspace engine options change
  useEffect(() => {
    const currentThemeId = getCurrentThemeId();
    const currentTokensForTheme = tokens[currentThemeId] || [];
    const allForTheme = [...(primitives || []).map(p => ({ ...p, isToken: false })), ...(currentTokensForTheme || []).map(t => ({ ...t, isToken: true }))];
    const engine = createColorEngine(allForTheme, contrastEngineOpts);
    setTokenPrecomputed(engine.computeAll());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTokenTheme, contrastEngineOpts]);

  // Rebuild primitive precomputed when workspace gamut / contrast metric changes
  // (token map already does this above; primitives had only been recomputed on bulk load)
  useEffect(() => {
    if ((primitives || []).length === 0) {
      setPrimitivePrecomputed(new Map());
      return;
    }
    const engine = createColorEngine(primitives, contrastEngineOpts);
    setPrimitivePrecomputed(engine.computeAll());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrastEngineOpts]);

  // After bulk loads, recompute token precomputed once
  useEffect(() => {
    if (isBulkLoadRef.current) {
      const currentThemeId = getCurrentThemeId();
      const currentTokensForTheme = tokens[currentThemeId] || [];
      const allForTheme = [...(primitives || []).map(p => ({ ...p, isToken: false })), ...(currentTokensForTheme || []).map(t => ({ ...t, isToken: true }))];
      const engine = createColorEngine(allForTheme, contrastEngineOpts);
      setTokenPrecomputed(engine.computeAll());
      isBulkLoadRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens, primitives, contrastEngineOpts]);

  // Static favicon using CSS variables (no JS calculations)
  useEffect(() => {
    const primaryHex = getHexFromCSSVar('--color-primary');
    const secondaryOnPrimaryHex = getHexFromCSSVar('--color-secondary-on-primary');
    updateFavicon(primaryHex, secondaryOnPrimaryHex).catch(console.error);
  }, []);

  // Set page title
  useEffect(() => {
    document.title = 'betterLCH — your color scheme';
  }, []);

  // Cleanup figma promo timer on unmount
  useEffect(() => {
    return () => {
      if (figmaPromoTimerRef.current) {
        clearTimeout(figmaPromoTimerRef.current);
      }
    };
  }, []);

  // Restore config from localStorage on mount (once)
  useEffect(() => {
    if (isRestoreAttemptedRef.current) return;
    isRestoreAttemptedRef.current = true;
    try {
      const raw = getStorage().getItem(CONFIG_STORAGE_KEY);
      if (!raw || raw.trim() === '') return;
      const data = JSON.parse(raw);
      if (!Array.isArray(data.primitives) || !Array.isArray(data.primitiveFolders)) return;
      if (!Array.isArray(data.colorThemes) || data.colorThemes.length === 0) return;
      if (typeof data.tokens !== 'object' || data.tokens === null) return;
      if (typeof data.tokenFolders !== 'object' || data.tokenFolders === null) return;
      isBulkLoadRef.current = true;
      shouldExpandFoldersRef.current = true;
      setPrimitives(data.primitives);
      setPrimitiveFolders(data.primitiveFolders || []);
      const newThemes = data.colorThemes;
      setColorThemes(newThemes);
      colorThemesRef.current = newThemes;
      setTokens(data.tokens);
      setTokenFolders(data.tokenFolders);
      const selected = data.selectedTokenTheme && newThemes.some(t => t.name === data.selectedTokenTheme)
        ? data.selectedTokenTheme
        : (newThemes[0]?.name || 'Default');
      setSelectedTokenTheme(selected);
      setPrimitivesSectionTitle(data.primitivesSectionTitle ?? 'Primitives');
      setThemesSectionTitle(data.themesSectionTitle ?? 'Semantic');
      setContrastAlgorithm(data.contrastAlgorithm === 'wcag' ? 'wcag' : 'apca');
      setColorSpace(normalizeColorSpace(data.colorSpace));
      setHasChosenStartScreen(true);
    } catch (e) {
      console.warn('Failed to restore config from storage', e);
    }
  }, []);

  // Keep ref updated for persistence (beforeunload and debounced save)
  useEffect(() => {
    stateForSaveRef.current = {
      primitives: primitives || [],
      primitiveFolders: primitiveFolders || [],
      colorThemes: colorThemes || [{ id: 'default', name: 'Default' }],
      tokens: tokens || {},
      tokenFolders: tokenFolders || {},
      selectedTokenTheme: selectedTokenTheme || 'Default',
      primitivesSectionTitle: primitivesSectionTitle ?? 'Primitives',
      themesSectionTitle: themesSectionTitle ?? 'Semantic',
      contrastAlgorithm: contrastAlgorithm === 'wcag' ? 'wcag' : 'apca',
      colorSpace: colorSpace === 'p3' ? 'p3' : 'srgb'
    };
  }, [primitives, primitiveFolders, colorThemes, tokens, tokenFolders, selectedTokenTheme, primitivesSectionTitle, themesSectionTitle, contrastAlgorithm, colorSpace]);

  // Debounced save to localStorage when in editor (not on start screen)
  useEffect(() => {
    if (showStartScreen) return;
    const hasContent = (primitives?.length > 0) || (colorThemes || []).some(t => {
      const list = tokens?.[t.id];
      const folders = tokenFolders?.[t.id];
      return (list?.length > 0) || (folders?.length > 0);
    });
    if (!hasContent) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const config = stateForSaveRef.current;
        getStorage().setItem(CONFIG_STORAGE_KEY, JSON.stringify(config));
      } catch (e) {
        console.warn('Failed to persist config', e);
      }
      saveTimeoutRef.current = null;
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [showStartScreen, primitives, primitiveFolders, colorThemes, tokens, tokenFolders, selectedTokenTheme, primitivesSectionTitle, themesSectionTitle, contrastAlgorithm, colorSpace]);

  // Save on page unload so latest state is persisted
  useEffect(() => {
    const onBeforeUnload = () => {
      const config = stateForSaveRef.current;
      const hasContent = (config.primitives?.length > 0) || (config.colorThemes || []).some(t => {
        const list = config.tokens?.[t.id];
        const folders = config.tokenFolders?.[t.id];
        return (list?.length > 0) || (folders?.length > 0);
      });
      if (hasContent) {
        try {
          getStorage().setItem(CONFIG_STORAGE_KEY, JSON.stringify(config));
        } catch (_) {}
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Compute primitive precomputed map on bulk loads (e.g., import) or first load
  useEffect(() => {
    if (isBulkLoadRef.current || primitivePrecomputed.size === 0) {
      if ((primitives || []).length > 0) {
        const engine = createColorEngine(primitives, contrastEngineOpts);
        setPrimitivePrecomputed(engine.computeAll());
      } else {
        setPrimitivePrecomputed(new Map());
      }
      isBulkLoadRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primitives, contrastEngineOpts]);

  // Expand all folders after bulk load completion (e.g., config import or restore from storage)
  useEffect(() => {
    if (!shouldExpandFoldersRef.current) return;
    const hasFolders = (primitiveFolders?.length > 0) || Object.values(tokenFolders || {}).some(arr => Array.isArray(arr) && arr.length > 0);
    if (hasFolders) {
      expandAllFolders();
      shouldExpandFoldersRef.current = false;
    }
    // If no folders yet (e.g. restore effect hasn't applied state), leave ref true so we expand on next run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primitiveFolders, tokenFolders]);

  const [affectedColors, setAffectedColors] = useState(new Set());

  // Separate UI state from data to prevent unnecessary rerenders
  const [folderUIState, setFolderUIState] = useState({
    primitiveFolders: new Map(), // Map<folderId, { isExpanded, isEditing }>
    tokenFolders: new Map()      // Map<folderId, { isExpanded, isEditing }>
  });

  // Helper to recompute only affected colors
  const recomputeAffectedColors = (colorId, opts = { isToken: false, arrays: null, alsoIds: [] }) => {
    const seed = Array.from(new Set([colorId, ...(opts.alsoIds || [])]));
    
    // Build a temporary engine from provided arrays (for up-to-date graph)
    if (opts.isToken) {
      const currentThemeId = getCurrentThemeId();
      const currentTokens = (opts.arrays?.tokens?.[currentThemeId]) || (tokens[currentThemeId] || []);
      const allForTheme = [ ...(primitives || []), ...(currentTokens || []) ];
      const engine = createColorEngine(allForTheme, contrastEngineOpts);
      const patch = engine.computeAffected(seed);
      setTokenPrecomputed(prev => mergeMaps(prev, patch));
    } else {
      // Optimize: Build combined graph once instead of twice
      const primArray = opts.arrays?.primitives || primitives;
      const currentThemeId = getCurrentThemeId();
      const currentTokens = (opts.arrays?.tokens?.[currentThemeId]) || (tokens[currentThemeId] || []);
      const allForTheme = [ ...(primArray || []), ...(currentTokens || []) ];
      
      // Build single engine with all colors (primitives + tokens)
      const combinedEngine = createColorEngine(allForTheme, contrastEngineOpts);
      const allAffected = combinedEngine.computeAffected(seed);
      
      // Split results into primitives and tokens patches
      const primPatch = new Map();
      const tokPatch = new Map();
      
      // Create sets for fast lookup
      const primIds = new Set((primArray || []).map(p => p.id));
      
      for (const [id, computed] of allAffected.entries()) {
        if (primIds.has(id)) {
          primPatch.set(id, computed);
        } else {
          tokPatch.set(id, computed);
        }
      }
      
      // Update both maps
      setPrimitivePrecomputed(prev => mergeMaps(prev, primPatch));
      setTokenPrecomputed(prev => mergeMaps(prev, tokPatch));
    }
    setAffectedColors(new Set([colorId]));
  };

  /**
   * Re-read each contrast color's actual contrast under a new {algorithm, colorSpace}
   * combination and write it back as the stored `contrastLevel`. Visual position of
   * the color stays the same — only the numeric target is rebased into the new metric.
   * This is the shared body for both the APCA/WCAG and sRGB/P3 toggles.
   */
  const rebaseContrastTargets = ({
    fromAlgorithm,
    fromColorSpace,
    toAlgorithm,
    toColorSpace
  }) => {
    const fromOpts = contrastEngineOptions(fromAlgorithm, fromColorSpace);
    const roundTarget = (v) => roundContrastLevel(v, toAlgorithm);

    const primEngine = createColorEngine(primitives, fromOpts);
    const primMap = primEngine.computeAll();
    const newPrimitives = primitives.map((p) => {
      if (p.mode !== 'contrast') return p;
      const result = primMap.get(p.id);
      const bg = primMap.get(p.backgroundColorId);
      if (!result?.hex || !bg?.hex) return p;
      // In P3 mode, measure on the full-precision OKLCH (preserves P3-only chroma)
      // so the rebased target matches the actual color, not its sRGB-clamped hex.
      const fgInput = colorInputForMeasurement(result, toColorSpace);
      const bgInput = colorInputForMeasurement(bg, toColorSpace);
      const v = measureContrastPair(fgInput, bgInput, toAlgorithm, toColorSpace);
      return { ...p, contrastLevel: roundTarget(v) };
    });

    const newTokens = { ...tokens };
    for (const theme of colorThemes) {
      const tid = theme.id;
      const list = tokens[tid] || [];
      if (list.length === 0) continue;
      const allForTheme = [
        ...newPrimitives.map((p) => ({ ...p, isToken: false })),
        ...list.map((t) => ({ ...t, isToken: true })),
      ];
      const eng = createColorEngine(allForTheme, fromOpts);
      const m = eng.computeAll();
      newTokens[tid] = list.map((t) => {
        if (t.mode !== 'contrast') return t;
        const result = m.get(t.id);
        const bg = m.get(t.backgroundColorId);
        if (!result?.hex || !bg?.hex) return t;
        const fgInput = colorInputForMeasurement(result, toColorSpace);
        const bgInput = colorInputForMeasurement(bg, toColorSpace);
        const v = measureContrastPair(fgInput, bgInput, toAlgorithm, toColorSpace);
        return { ...t, contrastLevel: roundTarget(v) };
      });
    }

    setPrimitives(newPrimitives);
    setTokens(newTokens);
  };

  /** Re-read contrast targets from current computed pairs, then switch global algorithm (colors stay stable). */
  const handleContrastAlgorithmToggle = () => {
    const next = contrastAlgorithm === 'apca' ? 'wcag' : 'apca';
    rebaseContrastTargets({
      fromAlgorithm: contrastAlgorithm,
      fromColorSpace: colorSpace,
      toAlgorithm: next,
      toColorSpace: colorSpace
    });
    setContrastAlgorithm(next);
  };

  /**
   * Switch workspace color space, keeping each color's target `contrastLevel` fixed.
   *
   * Design choice: a contrast target like "Lc 60" represents an intent — the user
   * wants 60 lightness contrast in the document's color space. When they toggle
   * sRGB → P3, the target stays at 60; the engine re-solves to land each color
   * at 60 Lc *in P3*, which means the rendered hex may shift by 1–2 Lc-worth.
   * (Compare to `handleContrastAlgorithmToggle`, which rebases the target so
   * colors stay visually stable across two fundamentally different metrics.)
   *
   * No-op effect for WCAG — its measurement is sRGB-only per spec, so the same
   * target produces the same colors regardless of this toggle.
   */
  const handleColorSpaceToggle = () => {
    const next = colorSpace === 'srgb' ? 'p3' : 'srgb';
    setColorSpace(next);
  };

  // Helper functions for UI state management
  const getFolderUIState = (folderId, isToken = false) => {
    if (!folderUIState) {
      return { isExpanded: false, isEditing: false };
    }
    const map = isToken ? folderUIState.tokenFolders : folderUIState.primitiveFolders;
    if (!map || typeof map.get !== 'function') {
      return { isExpanded: false, isEditing: false };
    }
    return map.get(folderId) || { isExpanded: false, isEditing: false };
  };

  const updateFolderUIState = (folderId, updates, isToken = false) => {
    setFolderUIState(prev => {
      const newState = { ...prev };
      const map = isToken ? newState.tokenFolders : newState.primitiveFolders;
      const newMap = new Map(map);
      const currentState = newMap.get(folderId) || { isExpanded: false, isEditing: false };
      newMap.set(folderId, { ...currentState, ...updates });
      
      if (isToken) {
        newState.tokenFolders = newMap;
      } else {
        newState.primitiveFolders = newMap;
      }
      
      return newState;
    });
  };

  // Helper function to expand all folders after config import
  const expandAllFolders = () => {
    setFolderUIState(prev => {
      const newState = { ...prev };
      
      // Expand all primitive folders
      const newPrimitiveMap = new Map();
      primitiveFolders.forEach(folder => {
        const currentState = prev.primitiveFolders?.get(folder.id) || { isExpanded: false, isEditing: false };
        newPrimitiveMap.set(folder.id, { ...currentState, isExpanded: true });
      });
      newState.primitiveFolders = newPrimitiveMap;
      
      // Expand all token folders for all themes
      const newTokenMap = new Map();
      Object.values(tokenFolders).forEach(themeFolders => {
        if (Array.isArray(themeFolders)) {
          themeFolders.forEach(folder => {
            const currentState = prev.tokenFolders?.get(folder.id) || { isExpanded: false, isEditing: false };
            newTokenMap.set(folder.id, { ...currentState, isExpanded: true });
          });
        }
      });
      newState.tokenFolders = newTokenMap;
      
      return newState;
    });
  };

  const handleImportConfigs = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    const doImport = () => {
      try {
        // Create file input element
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.style.display = 'none';
        
        input.onchange = async (event) => {
          handleImportFile(event, input);
        };
        
        // Trigger file selection
        document.body.appendChild(input);
        input.click();
      } catch (error) {
        console.error('Import failed:', error);
        alert('Import failed. Please try again.');
      }
    };

    // Always check for saved config first - MUST check localStorage synchronously
    const hasConfig = hasSavedConfig();
    
    if (hasConfig) {
      // Show modal first, file selector will open ONLY after confirmation
      isModalOpenRef.current = true;
      setUnsavedChangesModalVariant('import');
      setPendingAction(() => doImport);
      setShowUnsavedChangesModal(true);
      setShowStartScreenModal(false); // Close start screen modal
      // CRITICAL: Return early - do NOT open file selector here
      // The file selector will ONLY open when user clicks "Export and continue" or "Continue"
      return;
    }
    
    // Only proceed if no saved config exists
    setShowStartScreenModal(false); // Close start screen modal
    doImport();
  };

  const handleImportFile = async (event, inputElement) => {
    const file = event.target.files[0];
    if (!file) {
      if (inputElement && inputElement.parentNode === document.body) {
        document.body.removeChild(inputElement);
      }
      return;
    }
    
    // Clear all cached color values before import
    setPrimitivePrecomputed(new Map());
    setTokenPrecomputed(new Map());
    
    try {
      const text = await file.text();
      let config;
      try {
        config = JSON.parse(text);
      } catch (jsonError) {
        throw new Error(formatJsonImportError(jsonError, text));
      }
      
      // Validate configuration structure
      if (!config.data || !config.version) {
        throw new Error('Invalid configuration file format');
      }
      
      // Support both legacy format (primitives/tokens) and new collections format
      let { data } = config;
      const importedContrastAlgorithm = resolveContrastAlgorithm(config, data);
      const importedColorSpace = normalizeColorSpace(
        config?.settings?.colorSpace ?? data?.colorSpace
      );

      // Track section titles from collections
      let importedPrimitivesSectionTitle = null;
      let importedThemesSectionTitle = null;

      // If new collections-based format, convert it to the legacy shape
      if (data && Array.isArray(data.collections)) {
        const collections = data.collections;

        // Find Primitives and Themes collections.
        // 1) Prefer direct name/id matches
        // 2) Fallback to semantic structure heuristics for renamed collections from Figma.
        let primitivesCollection = collections.find(
          (c) => c.name === primitivesSectionTitle || c.name === 'Primitives' || c.id === 'primitives'
        );
        let tokensCollection = collections.find(
          (c) => c.name === themesSectionTitle || c.name === 'Themes' || c.id === 'themes' || c.id === 'tokens'
        );
        const scoreAsTokens = (c) => {
          const id = String(c?.id || '').toLowerCase();
          const name = String(c?.name || '').toLowerCase();
          const modes = Array.isArray(c?.modes) ? c.modes : [];
          let score = 0;
          if (id.includes('theme') || id.includes('token')) score += 5;
          if (name.includes('theme') || name.includes('semantic') || name.includes('token')) score += 5;
          if (modes.length > 1) score += 3;
          return score;
        };
        const scoreAsPrimitives = (c) => {
          const id = String(c?.id || '').toLowerCase();
          const name = String(c?.name || '').toLowerCase();
          const modes = Array.isArray(c?.modes) ? c.modes : [];
          let score = 0;
          if (id.includes('primitive') || id.includes('palette')) score += 5;
          if (name.includes('primitive') || name.includes('palette')) score += 5;
          if (modes.length === 1) score += 2;
          return score;
        };
        if (!tokensCollection && collections.length > 0) {
          if (collections.length === 1) {
            // Avoid misclassifying a single imported collection as Semantic.
            const only = collections[0];
            const tokenScore = scoreAsTokens(only);
            const primitiveScore = scoreAsPrimitives(only);
            if (tokenScore > primitiveScore && tokenScore >= 5) {
              tokensCollection = only;
            } else if (!primitivesCollection) {
              primitivesCollection = only;
            }
          } else {
            tokensCollection = [...collections].sort((a, b) => scoreAsTokens(b) - scoreAsTokens(a))[0];
          }
        }
        if (!primitivesCollection) {
          if (collections.length === 1) {
            if (!tokensCollection) primitivesCollection = collections[0];
          } else {
            const candidates = collections.filter((c) => c !== tokensCollection);
            primitivesCollection = [...candidates].sort((a, b) => scoreAsPrimitives(b) - scoreAsPrimitives(a))[0] || null;
          }
        }
        if (primitivesCollection && tokensCollection && primitivesCollection === tokensCollection && collections.length > 1) {
          const candidates = collections.filter((c) => c !== tokensCollection);
          primitivesCollection = [...candidates].sort((a, b) => scoreAsPrimitives(b) - scoreAsPrimitives(a))[0] || null;
        }

        // Extract section titles
        if (primitivesCollection) {
          importedPrimitivesSectionTitle = primitivesCollection.name;
        }
        if (tokensCollection) {
          importedThemesSectionTitle = tokensCollection.name;
        }

        const legacyData = { ...data, contrastAlgorithm: importedContrastAlgorithm };

        // Primitives: first mode of the primitives collection
        // If not present in import, preserve existing primitives
        if (primitivesCollection && Array.isArray(primitivesCollection.modes) && primitivesCollection.modes.length > 0) {
          const primMode = primitivesCollection.modes[0];
          legacyData.primitives = primMode.colors || [];
          legacyData.primitiveFolders = primMode.folders || [];
        } else {
          // Preserve existing primitives if not in import
          legacyData.primitives = primitives || [];
          legacyData.primitiveFolders = primitiveFolders || [];
        }

        // Process all collection modes to create themes
        const allThemes = [];
        const tokensByTheme = {};
        const tokenFoldersByTheme = {};

        // Helper to process modes and create themes
        const processModes = (collection, isThemesCollection = false) => {
          if (!collection || !Array.isArray(collection.modes)) return;
          
          collection.modes.forEach((mode) => {
            const modeName = mode.name || mode.id || 'Mode';

            // Try to match existing theme by name first (handles renames gracefully)
            const existingTheme = (legacyData.colorThemes || colorThemes || []).find(
              (t) => t.name === modeName
            );

            const themeId = (existingTheme && existingTheme.id) || mode.id || `theme-${modeName.toLowerCase().replace(/\s+/g, '-')}`;

            allThemes.push({ id: themeId, name: modeName });
            
            // For themes collection, store the colors as tokens
            // For primitives collection, we don't store tokens (primitives are global)
            if (isThemesCollection) {
              tokensByTheme[themeId] = mode.colors || [];
              tokenFoldersByTheme[themeId] = mode.folders || [];
            } else {
              // Primitives modes create themes but with empty tokens
              tokensByTheme[themeId] = tokensByTheme[themeId] || [];
              tokenFoldersByTheme[themeId] = tokenFoldersByTheme[themeId] || [];
            }
          });
        };

        // Handle themes based on what's being imported
        if (tokensCollection) {
          // Importing Themes collection - process and update themes
          processModes(tokensCollection, true);
          
          // Remove duplicates by name
          const uniqueThemes = [];
          const seenNames = new Set();
          for (const theme of allThemes) {
            if (!seenNames.has(theme.name)) {
              uniqueThemes.push(theme);
              seenNames.add(theme.name);
            }
          }
          legacyData.colorThemes = uniqueThemes;
          legacyData.tokens = tokensByTheme;
          legacyData.tokenFolders = tokenFoldersByTheme;
        } else if (primitivesCollection && !tokensCollection) {
          // Importing Primitives only - preserve existing themes
          // Don't create themes from Primitives modes, keep existing
          legacyData.colorThemes = colorThemes || [{ id: 'default', name: 'Default' }];
          legacyData.tokens = tokens || { 'default': [] };
          legacyData.tokenFolders = tokenFolders || { 'default': [] };
        } else {
          // No collections in import - preserve existing
          legacyData.colorThemes = colorThemes || [{ id: 'default', name: 'Default' }];
          legacyData.tokens = tokens || { 'default': [] };
          legacyData.tokenFolders = tokenFolders || { 'default': [] };
        }

        // Preserve selectedTokenTheme if present
        if (!legacyData.selectedTokenTheme && Array.isArray(legacyData.colorThemes) && legacyData.colorThemes.length > 0) {
          legacyData.selectedTokenTheme = legacyData.colorThemes[0].name;
        }

        // Replace data with normalized legacy data
        data = legacyData;
        config.data = legacyData;
      }

      data.contrastAlgorithm = importedContrastAlgorithm;

      // Validate required fields exist and are correct types
      if (!Array.isArray(data.primitives)) {
        throw new Error('Configuration file has invalid primitives data');
      }
      if (!Array.isArray(data.primitiveFolders)) {
        data.primitiveFolders = []; // Allow missing primitive folders
      }
      // Note: colorThemes should always exist (created from Primitives "Mode 1" or Themes modes)
      // but we'll add a safety check with a helpful message
      if (!Array.isArray(data.colorThemes) || data.colorThemes.length === 0) {
        throw new Error('Configuration file has invalid structure. Please ensure at least one mode/theme exists in your collections.');
      }
      if (typeof data.tokens !== 'object' || data.tokens === null) {
        throw new Error('Configuration file has invalid tokens data');
      }
      if (typeof data.tokenFolders !== 'object' || data.tokenFolders === null) {
        throw new Error('Configuration file has invalid token folders data');
      }

      // Fill sparse / incomplete color entries (ids, names, defaults, refs)
      const hydrated = hydrateImportedData(data, {
        contrastAlgorithm: importedContrastAlgorithm
      });
      data = hydrated.data;
      config.data = data;
      if (hydrated.warnings.length > 0) {
        console.warn('Import hydration warnings:', hydrated.warnings);
        alert(`Import completed with warnings:\n${hydrated.warnings.slice(0, 8).join('\n')}`);
      }
      
      // Import all data (mark as bulk load to trigger full recompute once)
      isBulkLoadRef.current = true;
      shouldExpandFoldersRef.current = true; // Flag to expand folders after import
      
      // Set primitives
      setPrimitives(data.primitives || []);
      setPrimitiveFolders(data.primitiveFolders || []);
      setContrastAlgorithm(data.contrastAlgorithm === 'wcag' ? 'wcag' : 'apca');
      setColorSpace(importedColorSpace);
      
      // Normalize to single theme when in single-theme mode
      const dataToApply = singleTheme ? normalizeToSingleThemeData(data) : data;
      
      // Set theme system
      const newThemes = dataToApply.colorThemes || [{ id: 'default', name: 'Default' }];
      setColorThemes(newThemes);
      colorThemesRef.current = newThemes; // Update ref immediately
      console.log('[File Import - Replace] Updated colorThemesRef to:', newThemes.map(t => t.name).join(', '));
      
      // Set theme-specific data
      setTokens(dataToApply.tokens || {});
      setTokenFolders(dataToApply.tokenFolders || {});
      
      // Restore section titles if present in import
      if (importedPrimitivesSectionTitle) {
        setPrimitivesSectionTitle(importedPrimitivesSectionTitle);
      }
      if (importedThemesSectionTitle) {
        setThemesSectionTitle(importedThemesSectionTitle);
      }
      
      // Set selected theme
      const targetTheme = dataToApply.selectedTokenTheme || dataToApply.colorThemes[0]?.name || 'Default';
      const themeExists = dataToApply.colorThemes.some(theme => theme.name === targetTheme);
      setSelectedTokenTheme(themeExists ? targetTheme : dataToApply.colorThemes[0]?.name || 'Default');
      
      // Import successful — no alert needed
    } catch (parseError) {
      console.error('Import parsing failed:', parseError);
      alert(`Import failed: ${parseError.message}`);
    } finally {
      // Clean up
      if (inputElement && inputElement.parentNode === document.body) {
        document.body.removeChild(inputElement);
      }
    }
  };

  // Export from saved config (used when on start screen)
  const exportFromSavedConfig = async () => {
    try {
      const raw = getStorage().getItem(CONFIG_STORAGE_KEY);
      if (!raw || raw.trim() === '') {
        alert('No saved configuration to export.');
        return;
      }
      const data = JSON.parse(raw);
      const savedColorSpace = normalizeColorSpace(data.colorSpace);

      const exportData = buildFigmaExportPayload({
        primitives: data.primitives || [],
        primitiveFolders: data.primitiveFolders || [],
        tokens: data.tokens || {},
        tokenFolders: data.tokenFolders || {},
        colorThemes: data.colorThemes || [],
        primitivesSectionTitle: data.primitivesSectionTitle || 'Primitives',
        themesSectionTitle: data.themesSectionTitle || 'Semantic',
        contrastAlgorithm: data.contrastAlgorithm,
        colorSpace: savedColorSpace,
      });

      downloadJsonFile(exportData, `betterLCH - Color Scheme ${formatExportTimestamp()}.json`);
    } catch (error) {
      console.error('Export from saved config failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const handleExport = () => {
    try {
      const exportData = buildFigmaExportPayload({
        primitives,
        primitiveFolders,
        tokens,
        tokenFolders,
        colorThemes,
        primitivesSectionTitle,
        themesSectionTitle,
        contrastAlgorithm,
        colorSpace,
      });
      downloadJsonFile(exportData, `betterLCH - Color Scheme ${formatExportTimestamp()}.json`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Import from Figma Plugin (new format with ID mapping)
  const handleImportFigmaPlugin = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Clear all cached color values before import
      setPrimitivePrecomputed(new Map());
      setTokenPrecomputed(new Map());

      try {
        let importData = await readJsonFile(file);

        // Support both old format and new collections format
        if (importData.data && Array.isArray(importData.data.collections)) {
          // New collections format - convert to flat format
          const converted = importFromFigmaPlugin(importData, {
            primitives,
            primitiveFolders,
            tokens,
            tokenFolders,
            colorThemes,
            primitivesSectionTitle,
            themesSectionTitle,
            contrastAlgorithm,
            colorSpace
          });
          
          // Update state with converted data
          console.log('[Import] Setting colorThemes to:', converted.colorThemes.map(t => t.name));
          setPrimitives(converted.primitives);
          setPrimitiveFolders(converted.primitiveFolders);
          setTokens(converted.tokens);
          setTokenFolders(converted.tokenFolders);
          setColorThemes(converted.colorThemes);
          // Restore section titles if present
          if (converted.primitivesSectionTitle) {
            setPrimitivesSectionTitle(converted.primitivesSectionTitle);
          }
          if (converted.themesSectionTitle) {
            setThemesSectionTitle(converted.themesSectionTitle);
          }
          setContrastAlgorithm(converted.contrastAlgorithm === 'wcag' ? 'wcag' : 'apca');
          setColorSpace(normalizeColorSpace(converted.colorSpace));
          // IMPORTANT: Also update ref immediately for synchronous access
          colorThemesRef.current = converted.colorThemes;
          console.log('[Import] colorThemesRef.current now:', colorThemesRef.current.map(t => t.name));
          
          // Recompute all colors after import
          isBulkLoadRef.current = true;
          
          // Import successful
          return;
        }

        // Old format validation
        if (!importData.version || !importData.primitives || !importData.tokens) {
          throw new Error('Invalid import file format');
        }

        // Import data (old format)
        const updatedState = importFromFigmaPlugin(importData, {
          primitives,
          primitiveFolders,
          tokens,
          tokenFolders,
          colorThemes,
          primitivesSectionTitle,
          themesSectionTitle,
          contrastAlgorithm,
          colorSpace
        });

        // Update state
        console.log('[Import Old Format] Setting colorThemes to:', updatedState.colorThemes.map(t => t.name));
        setPrimitives(updatedState.primitives);
        setPrimitiveFolders(updatedState.primitiveFolders);
        setTokens(updatedState.tokens);
        setTokenFolders(updatedState.tokenFolders);
        setColorThemes(updatedState.colorThemes);
        // Restore section titles if present
        if (updatedState.primitivesSectionTitle) {
          setPrimitivesSectionTitle(updatedState.primitivesSectionTitle);
        }
        if (updatedState.themesSectionTitle) {
          setThemesSectionTitle(updatedState.themesSectionTitle);
        }
        setContrastAlgorithm(updatedState.contrastAlgorithm === 'wcag' ? 'wcag' : 'apca');
        setColorSpace(normalizeColorSpace(updatedState.colorSpace));
        // IMPORTANT: Also update ref immediately for synchronous access
        colorThemesRef.current = updatedState.colorThemes;
        console.log('[Import Old Format] colorThemesRef.current now:', colorThemesRef.current.map(t => t.name));

        // Recompute all colors after import
        isBulkLoadRef.current = true;
        
        // Import successful
      } catch (error) {
        console.error('Figma plugin import failed:', error);
        alert(`Import failed: ${error.message}`);
      }
    };
    input.click();
  };

  // Utility functions for generating unique IDs
  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Helper: Ensure folder tree exists from folderPath, creating missing folders
  // Returns the folderId of the deepest folder in the path
  // NOTE: This is purely path-based - it matches folders by (name, parentId), not by ID
  // This allows Figma folder reorganization to sync properly to the app
  const ensureFolderTree = (folderPath, foldersMap, generateId) => {
    if (!folderPath || folderPath.trim() === '') {
      return null; // No folder path, return null
    }

    const segments = folderPath.split('/').filter(Boolean);
    if (segments.length === 0) {
      return null;
    }

    let currentParentId = null;
    let currentFolderId = null;

    // Walk through each segment of the path
    for (let i = 0; i < segments.length; i++) {
      const segmentName = segments[i];

      // Look for existing folder with this name at the current level
      // This is path-based matching: we find folders by (name + parentId), not by ID
      let existingFolder = null;
      for (const [id, folder] of foldersMap.entries()) {
        if (folder.name === segmentName && folder.parentId === currentParentId) {
          existingFolder = folder;
          break;
        }
      }

      if (existingFolder) {
        // Folder exists at this path level, reuse it
        currentFolderId = existingFolder.id;
        currentParentId = existingFolder.id;
      } else {
        // Folder doesn't exist at this path level, create it with new ID
        const newFolderId = generateId();

        const newFolder = {
          id: newFolderId,
          name: segmentName,
          parentId: currentParentId,
          isExpanded: true,
          isEditing: false
        };

        foldersMap.set(newFolderId, newFolder);
        currentFolderId = newFolderId;
        currentParentId = newFolderId;
      }
    }

    return currentFolderId; // Return the deepest folder's ID
  };


  // Theme management functions
  const createTheme = (name) => {
    const newThemeId = generateId();
    const newTheme = { id: newThemeId, name };
    
    // Get the default (first) theme's data to copy from
    const defaultThemeId = colorThemes[0]?.id || 'default';
    const defaultTokens = tokens[defaultThemeId] || [];
    const defaultFolders = tokenFolders[defaultThemeId] || [];
    
    // Add new theme
    setColorThemes(prev => {
      const updated = [...prev, newTheme];
      colorThemesRef.current = updated; // Update ref immediately
      console.log('[createTheme] Updated colorThemesRef to:', updated.map(t => t.name).join(', '));
      return updated;
    });
    
    // Initialize new theme with copies of default theme's tokens and folders
    // IMPORTANT: Keep the same folder IDs across themes so tokens can reference them properly
    setTokens(prev => ({ 
      ...prev, 
      [newThemeId]: defaultTokens.map(token => ({ ...token })) // Deep copy tokens
    }));
    setTokenFolders(prev => ({ 
      ...prev, 
      [newThemeId]: defaultFolders.map(folder => ({ ...folder })) // Deep copy folders with same IDs
    }));
    
    // Switch to new theme
    setSelectedTokenTheme(name);
    
  };

  const renameTheme = (themeId, newName) => {
    setColorThemes(prev => {
      const updated = prev.map(theme => 
        theme.id === themeId ? { ...theme, name: newName } : theme
      );
      colorThemesRef.current = updated; // Update ref immediately
      console.log('[renameTheme] Updated colorThemesRef to:', updated.map(t => t.name).join(', '));
      return updated;
    });
    
    // Update selected theme if it was renamed
    const currentTheme = getCurrentTheme();
    if (currentTheme?.id === themeId) {
      setSelectedTokenTheme(newName);
    }
  };

  const deleteTheme = (themeId) => {
    // Don't allow deleting the last theme
    if (colorThemes.length <= 1) {
      alert('Cannot delete the last theme');
      return;
    }
    
    const theme = colorThemes.find(t => t.id === themeId);
    const themeName = theme ? theme.name : 'this theme';
    
    showConfirmation({
      title: 'Delete Theme',
      message: `Are you sure you want to delete "${themeName}"? All theme colors in this theme will be lost. This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: () => {
        // Remove theme from list
        setColorThemes(prev => {
          const updated = prev.filter(theme => theme.id !== themeId);
          colorThemesRef.current = updated; // Update ref immediately
          console.log('[deleteTheme] Updated colorThemesRef to:', updated.map(t => t.name).join(', '));
          return updated;
        });
        
        // Remove theme data
        setTokens(prev => {
          const newTokens = { ...prev };
          delete newTokens[themeId];
          return newTokens;
        });
        setTokenFolders(prev => {
          const newFolders = { ...prev };
          delete newFolders[themeId];
          return newFolders;
        });
        
        // Switch to first available theme if current theme was deleted
        const currentTheme = getCurrentTheme();
        if (currentTheme?.id === themeId) {
          const remainingThemes = colorThemes.filter(theme => theme.id !== themeId);
          setSelectedTokenTheme(remainingThemes[0]?.name || 'Default');
        }
      }
    });
  };

  // Color/Folder manipulation functions
  const updatePrimitive = (id, updates) => {
    setPrimitives(prev => {
      const updated = prev.map(item => item.id === id ? { ...item, ...updates } : item);
      // Incremental recompute on updated arrays
      recomputeAffectedColors(id, { isToken: false, arrays: { primitives: updated } });
      return updated;
    });
  };

  const updateToken = (id, updates) => {
    // Determine which properties should be synchronized across themes
    const structuralProps = ['name', 'folderId'];
    const themeSpecificProps = ['mode', 'colorValue', 'hexValue', 'oklchValue', 'p3Support', 'referenceColorId', 'lExpression', 'cExpression', 'hExpression', 'aExpression', 'backgroundColorId', 'foregroundColorId', 'contrastLevel', 'useAlpha'];
    
    const structuralUpdates = {};
    const themeSpecificUpdates = {};
    
    // Separate updates into structural and theme-specific
    Object.keys(updates).forEach(key => {
      if (structuralProps.includes(key)) {
        structuralUpdates[key] = updates[key];
      } else if (themeSpecificProps.includes(key)) {
        themeSpecificUpdates[key] = updates[key];
      } else {
        // Default to structural for unknown properties
        structuralUpdates[key] = updates[key];
      }
    });
    
    setTokens(prev => {
      const newTokens = { ...prev };
      
      // Apply structural updates to ALL themes
      if (Object.keys(structuralUpdates).length > 0) {
        colorThemes.forEach(theme => {
          const themeId = theme.id;
          if (newTokens[themeId]) {
            newTokens[themeId] = newTokens[themeId].map(item => 
              item.id === id ? { ...item, ...structuralUpdates } : item
            );
          }
        });
      }
      
      // Apply theme-specific updates only to current theme
      if (Object.keys(themeSpecificUpdates).length > 0) {
        const currentThemeId = getCurrentThemeId();
        if (newTokens[currentThemeId]) {
          newTokens[currentThemeId] = newTokens[currentThemeId].map(item => 
            item.id === id ? { ...item, ...themeSpecificUpdates } : item
          );
        }
      }
      
      // Trigger incremental recomputation for affected colors (current theme)
      recomputeAffectedColors(id, { isToken: true, arrays: { tokens: newTokens } });
      
      return newTokens;
    });
  };

  const updatePrimitiveFolder = (id, updates) => {
    setPrimitiveFolders(prev => prev.map(folder => 
      folder.id === id ? { ...folder, ...updates } : folder
    ));
  };

  const updateTokenFolder = (id, updates) => {
    // Update folder across ALL themes to keep folder names synchronized
    setTokenFolders(prev => {
      const newFolders = { ...prev };
      colorThemes.forEach(theme => {
        const themeId = theme.id;
        if (newFolders[themeId]) {
          newFolders[themeId] = newFolders[themeId].map(folder => 
            folder.id === id ? { ...folder, ...updates } : folder
          );
        }
      });
      return newFolders;
    });
  };

  /** Default section title when empty: "Collection" or "Collection 2" so the two sections stay unique. */
  const getDefaultSectionTitle = (otherSectionTitle) =>
    otherSectionTitle === 'Collection' ? 'Collection 2' : 'Collection';

  /** Unique folder name among siblings: "Folder", "Folder 2", "Folder 3", ... */
  const getUniqueFolderName = (folders, parentId, excludeFolderId) => {
    const siblingNames = new Set(
      (folders || [])
        .filter(f => f.parentId === parentId && f.id !== excludeFolderId)
        .map(f => f.name)
    );
    if (!siblingNames.has('Folder')) return 'Folder';
    let n = 2;
    while (siblingNames.has(`Folder ${n}`)) n++;
    return `Folder ${n}`;
  };

  /** Unique color name within the same folder (root when folderId is null): "Color", "Color 2", … */
  const getUniqueColorName = (colors, excludeColorId, folderId = null) => {
    const targetFolderId = folderId || null;
    const usedNames = new Set(
      (colors || [])
        .filter((c) => c.id !== excludeColorId && (c.folderId || null) === targetFolderId)
        .map((c) => c.name)
    );
    if (!usedNames.has('Color')) return 'Color';
    let n = 2;
    while (usedNames.has(`Color ${n}`)) n++;
    return `Color ${n}`;
  };

  // Move/Delete functions
  const movePrimitive = (id, direction) => {
    setPrimitives(prev => {
      const index = prev.findIndex(item => item.id === id);
      if (index === -1) {
        console.warn(`Primitive with id ${id} not found`);
        return prev;
      }

      const item = prev[index];
      const parentFolderId = item.folderId || null;

      // Only reorder within the same folder (including root),
      // based on the visual order of siblings.
      const siblingIndices = prev
        .map((p, idx) => ({ p, idx }))
        .filter(({ p }) => (p.folderId || null) === parentFolderId)
        .map(({ idx }) => idx);

      if (siblingIndices.length <= 1) {
        return prev;
      }

      const siblingPosition = siblingIndices.indexOf(index);
      const targetPosition = direction === 'up' ? siblingPosition - 1 : siblingPosition + 1;
      if (targetPosition < 0 || targetPosition >= siblingIndices.length) {
        console.warn(
          `Cannot move primitive ${direction}: already at ${
            direction === 'up' ? 'top' : 'bottom'
          } of its folder`
        );
        return prev;
      }

      // Rebuild the sibling sequence with swapped positions, then
      // write it back into the original array at the same indexes.
      const siblingItems = siblingIndices.map(i => prev[i]);
      const newSiblingItems = [...siblingItems];
      [newSiblingItems[siblingPosition], newSiblingItems[targetPosition]] = [
        newSiblingItems[targetPosition],
        newSiblingItems[siblingPosition],
      ];

      const next = [...prev];
      siblingIndices.forEach((idx, i) => {
        next[idx] = newSiblingItems[i];
      });
      return next;
    });
  };

  const moveToken = (id, direction) => {
    // Move token across ALL themes to maintain synchronization
    setTokens(prev => {
      const newTokens = { ...prev };

      // Use the current theme as the reference for which sibling to reorder
      const currentThemeId = getCurrentThemeId();
      const currentTokens = newTokens[currentThemeId] || [];
      const index = currentTokens.findIndex(item => item.id === id);

      if (index === -1) {
        console.warn(`Token with id ${id} not found in current theme`);
        return prev;
      }

      const item = currentTokens[index];
      const parentFolderId = item.folderId || null;

      // Only reorder within the same folder (including root)
      const siblingIndices = currentTokens
        .map((t, idx) => ({ t, idx }))
        .filter(({ t }) => (t.folderId || null) === parentFolderId)
        .map(({ idx }) => idx);

      if (siblingIndices.length <= 1) {
        return prev;
      }

      const siblingPosition = siblingIndices.indexOf(index);
      const targetPosition = direction === 'up' ? siblingPosition - 1 : siblingPosition + 1;
      if (targetPosition < 0 || targetPosition >= siblingIndices.length) {
        console.warn(
          `Cannot move token ${direction}: already at ${
            direction === 'up' ? 'top' : 'bottom'
          } of its folder`
        );
        return prev;
      }

      // Determine the new desired order of sibling IDs based on current theme
      const siblingIds = siblingIndices.map(i => currentTokens[i].id);
      const newSiblingIds = [...siblingIds];
      [newSiblingIds[siblingPosition], newSiblingIds[targetPosition]] = [
        newSiblingIds[targetPosition],
        newSiblingIds[siblingPosition],
      ];

      // Apply the same sibling ID order to all themes
      let movedInAnyTheme = false;
      colorThemes.forEach(theme => {
        const themeId = theme.id;
        const themeTokens = [...(newTokens[themeId] || [])];

        // Map of sibling items by ID for this theme
        const siblingItemsById = new Map(
          themeTokens
            .filter(t => siblingIds.includes(t.id))
            .map(t => [t.id, t])
        );

        if (siblingItemsById.size === 0) {
          return;
        }

        // Existing positions of these siblings in this theme
        const themeSiblingIndices = themeTokens
          .map((t, idx) => ({ t, idx }))
          .filter(({ t }) => siblingIds.includes(t.id))
          .map(({ idx }) => idx);

        if (themeSiblingIndices.length <= 1) {
          return;
        }

        const reorderedSiblingItems = newSiblingIds
          .filter(sid => siblingItemsById.has(sid))
          .map(sid => siblingItemsById.get(sid));

        if (reorderedSiblingItems.length !== themeSiblingIndices.length) {
          return;
        }

        const nextThemeTokens = [...themeTokens];
        themeSiblingIndices.forEach((idx, i) => {
          nextThemeTokens[idx] = reorderedSiblingItems[i];
        });

        newTokens[themeId] = nextThemeTokens;
        movedInAnyTheme = true;
      });

      if (!movedInAnyTheme) {
        console.warn(`Token ${id} was not found in any theme or could not be moved`);
        return prev;
      }

      return newTokens;
    });
  };

  // Reference check: color c references targetId as Reference/Background/Foreground
  const referencesColorId = (c, targetId) =>
    c.id !== targetId &&
    (c.referenceColorId === targetId || c.backgroundColorId === targetId || c.foregroundColorId === targetId);

  const isPrimitiveReferenced = (primitiveId) => {
    if (primitives.some((c) => referencesColorId(c, primitiveId))) return true;
    for (const themeId of Object.keys(tokens)) {
      const list = tokens[themeId] || [];
      if (list.some((c) => referencesColorId(c, primitiveId))) return true;
    }
    return false;
  };

  const isTokenReferenced = (tokenId) => {
    for (const themeId of Object.keys(tokens)) {
      const list = tokens[themeId] || [];
      if (list.some((c) => referencesColorId(c, tokenId))) return true;
    }
    return false;
  };

  const primitiveFolderContainsReferencedColor = (folderId, recursive = false) => {
    const folderIds = recursive
      ? (() => {
          const collect = (id) => {
            const children = primitiveFolders.filter((f) => f.parentId === id);
            return [id, ...children.flatMap((ch) => collect(ch.id))];
          };
          return collect(folderId);
        })()
      : [folderId];
    const primitiveIdsInFolder = primitives
      .filter((p) => folderIds.includes(p.folderId))
      .map((p) => p.id);
    return primitiveIdsInFolder.some((pid) => isPrimitiveReferenced(pid));
  };

  const tokenFolderContainsReferencedColor = (folderId, recursive = false) => {
    const currentThemeId = getCurrentThemeId();
    const currentFolders = tokenFolders[currentThemeId] || [];
    const folderIds = recursive
      ? (() => {
          const collect = (id) => {
            const children = currentFolders.filter((f) => f.parentId === id);
            return [id, ...children.flatMap((ch) => collect(ch.id))];
          };
          return collect(folderId);
        })()
      : [folderId];
    const currentTokens = tokens[currentThemeId] || [];
    const tokenIdsInFolder = currentTokens
      .filter((t) => folderIds.includes(t.folderId))
      .map((t) => t.id);
    return tokenIdsInFolder.some((tid) => isTokenReferenced(tid));
  };

  const deletePrimitive = (id) => {
    const color = primitives.find((c) => c.id === id);
    const colorName = color ? color.name : 'this color';

    const doDelete = () => {
      setPrimitives((prev) => prev.filter((item) => item.id !== id));
    };

    if (isPrimitiveReferenced(id)) {
      showConfirmation({
        title: 'Delete Color?',
        message: `"${colorName}" is used as Reference, Background, or Foreground by other colors. Delete anyway? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: doDelete
      });
    } else {
      doDelete();
    }
  };

  const clearPrimitiveEditing = (id) => {
    setPrimitives(prev => prev.map(item => 
      item.id === id ? { ...item, isEditing: false } : item
    ));
  };

  const clearTokenEditing = (id) => {
    setTokens(prev => {
      const newTokens = { ...prev };
      const currentThemeId = getCurrentThemeId();
      if (newTokens[currentThemeId]) {
        newTokens[currentThemeId] = newTokens[currentThemeId].map(item => 
          item.id === id ? { ...item, isEditing: false } : item
        );
      }
      return newTokens;
    });
  };

  const duplicatePrimitive = (id) => {
    const original = primitives.find(item => item.id === id);
    if (!original) return;

    const duplicated = {
      ...original,
      id: generateId(),
      name: original.name + ' copy',
      isEditing: true,
      // Clear Figma IDs so duplicate is treated as new color
      figmaVariableId: undefined
    };

    // Find the index of the original item
    const originalIndex = primitives.findIndex(item => item.id === id);
    
    setPrimitives(prev => {
      const newArray = [...prev];
      newArray.splice(originalIndex + 1, 0, duplicated);
      // Recompute only duplicated primitive and its dependants
      recomputeAffectedColors(duplicated.id, { isToken: false, arrays: { primitives: newArray } });
      return newArray;
    });
  };

  const deleteToken = (id) => {
    const currentThemeId = getCurrentThemeId();
    const currentTokens = tokens[currentThemeId] || [];
    const color = currentTokens.find((c) => c.id === id);
    const colorName = color ? color.name : 'this color';

    const doDelete = () => {
      setTokens((prev) => {
        const newTokens = { ...prev };
        colorThemes.forEach((theme) => {
          const themeId = theme.id;
          newTokens[themeId] = (newTokens[themeId] || []).filter((item) => item.id !== id);
        });
        return newTokens;
      });
    };

    if (isTokenReferenced(id)) {
      showConfirmation({
        title: 'Delete Color?',
        message: `"${colorName}" is used as Reference, Background, or Foreground by other colors. Delete anyway? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: doDelete
      });
    } else {
      doDelete();
    }
  };

  const duplicateToken = (id) => {
    const currentThemeId = getCurrentThemeId();
    const currentTokens = tokens[currentThemeId] || [];
    const original = currentTokens.find(item => item.id === id);
    if (!original) return;

    const duplicated = {
      ...original,
      id: generateId(),
      name: original.name + ' copy',
      isEditing: true,
      // Clear Figma IDs so duplicate is treated as new color
      figmaVariableId: undefined
    };

    // Find the index of the original item
    const originalIndex = currentTokens.findIndex(item => item.id === id);
    
    // Get folder info if token is in a folder
    const originalFolderId = original.folderId;
    let folderName = null;
    let folderParentId = null;
    if (originalFolderId) {
      const currentFolders = tokenFolders[currentThemeId] || [];
      const originalFolder = currentFolders.find(f => f.id === originalFolderId);
      if (originalFolder) {
        folderName = originalFolder.name;
        folderParentId = originalFolder.parentId;
      }
    }
    
    // Add duplicated token to ALL themes at the same position
    setTokens(prev => {
      const newTokens = { ...prev };
      const currentThemes = colorThemesRef.current; // Always gets latest themes!
      console.log('[duplicateToken] Duplicating token to themes:', currentThemes.map(t => t.name).join(', '));
      
      currentThemes.forEach(theme => {
        // Initialize theme if it doesn't exist
        if (!newTokens[theme.id]) {
          newTokens[theme.id] = [];
        }
        
        // Find matching folder in this theme
        let themeFolderId = originalFolderId;
        if (folderName) {
          const themeFolders = tokenFolders[theme.id] || [];
          const matchingFolder = themeFolders.find(f => 
            f.name === folderName && f.parentId === folderParentId
          );
          themeFolderId = matchingFolder ? matchingFolder.id : originalFolderId;
        }
        
        // Create duplicated token with theme-specific folder ID
        const duplicatedForTheme = {
          ...duplicated,
          folderId: themeFolderId
        };
        
        // Insert at the same position in all themes
        const themeTokens = [...(newTokens[theme.id] || [])];
        themeTokens.splice(originalIndex + 1, 0, duplicatedForTheme);
        newTokens[theme.id] = themeTokens;
        console.log(`[duplicateToken] Added duplicate "${duplicated.name}" to theme "${theme.name}" (folderId: ${themeFolderId})`);
      });
      
      // Recompute only duplicated token for current theme
      recomputeAffectedColors(duplicated.id, { isToken: true, arrays: { tokens: newTokens } });
      return newTokens;
    });
  };

  // Move a token into a folder across ALL themes (create folder if missing, or remove from folder)
  const moveTokenAcrossThemes = (tokenId, targetFolderName) => {
    const trimmedName = (targetFolderName || '').trim();

    // If removing from folder, just clear folderId across themes
    if (trimmedName === '') {
      setTokens(prevTokens => {
        const newTokens = { ...prevTokens };
        colorThemes.forEach(theme => {
          const themeId = theme.id;
          newTokens[themeId] = (newTokens[themeId] || []).map(t =>
            t.id === tokenId ? { ...t, folderId: null } : t
          );
        });
        return newTokens;
      });
      return;
    }

    // Determine a canonical folder ID (reuse existing by name if any, else create a new one)
    let canonicalFolderId = null;
    for (const theme of colorThemes) {
      const themeId = theme.id;
      const foldersInTheme = tokenFolders[themeId] || [];
      const existing = foldersInTheme.find(f => f.name === trimmedName);
      if (existing) {
        canonicalFolderId = existing.id;
        break;
      }
    }
    if (!canonicalFolderId) {
      canonicalFolderId = generateId();
    }

    // Ensure the folder with canonicalFolderId exists in every theme
    setTokenFolders(prevFolders => {
      const newFolders = { ...prevFolders };
      colorThemes.forEach(theme => {
        const themeId = theme.id;
        const folders = [...(newFolders[themeId] || [])];
        const existingByName = folders.find(f => f.name === trimmedName);
        if (existingByName) {
          // If folder exists but with different id, unify ids across themes
          if (existingByName.id !== canonicalFolderId) {
            const updated = folders.map(f =>
              f.name === trimmedName ? { ...f, id: canonicalFolderId } : f
            );
            newFolders[themeId] = updated;
          } else {
            newFolders[themeId] = folders;
          }
        } else {
          folders.push({ id: canonicalFolderId, name: trimmedName, isExpanded: true, isEditing: false });
          newFolders[themeId] = folders;
        }
      });
      return newFolders;
    });

    // Assign the token to the canonical folder across themes
    setTokens(prevTokens => {
      const newTokens = { ...prevTokens };
      colorThemes.forEach(theme => {
        const themeId = theme.id;
        newTokens[themeId] = (newTokens[themeId] || []).map(t =>
          t.id === tokenId ? { ...t, folderId: canonicalFolderId } : t
        );
      });
      return newTokens;
    });
  };

  // Helper function to move primitive to folder
  const movePrimitiveToFolder = (primitiveId, targetFolderId) => {
    if (targetFolderId === null) {
      // Move to root (no folder)
      updatePrimitive(primitiveId, { folderId: null });
    } else {
      // Move to specific folder
      updatePrimitive(primitiveId, { folderId: targetFolderId });
    }
  };

  // Helper function to move token to folder
  const moveTokenToFolder = (tokenId, targetFolderId) => {
    if (targetFolderId === null) {
      // Move to root (no folder)
      updateToken(tokenId, { folderId: null });
    } else {
      // Move to specific folder
      updateToken(tokenId, { folderId: targetFolderId });
    }
  };

  // Helper function to move primitive folder to another primitive folder
  const moveFolderToFolder = (folderId, targetFolderId) => {
    if (targetFolderId === null) {
      // Move to root (no parent)
      updatePrimitiveFolder(folderId, { parentId: null });
    } else {
      // Move to specific folder
      updatePrimitiveFolder(folderId, { parentId: targetFolderId });
    }
  };

  // Helper function to move token folder to another token folder across ALL themes
  const moveTokenFolderToFolder = (folderId, targetFolderId) => {
    const currentThemeId = getCurrentThemeId();
    const currentFolders = tokenFolders[currentThemeId] || [];

    // Prevent moving into itself or its descendants
    const collectDescendants = (id) => {
      const children = currentFolders.filter(f => f.parentId === id);
      let acc = [id];
      children.forEach(ch => { acc = acc.concat(collectDescendants(ch.id)); });
      return acc;
    };
    const blockedIds = collectDescendants(folderId);
    if (targetFolderId && blockedIds.includes(targetFolderId)) {
      console.warn('Cannot move a folder inside itself or its descendant');
      return;
    }

    updateTokenFolder(folderId, { parentId: targetFolderId === null ? null : targetFolderId });
  };

  // Primitive folder move modal handlers
  const handleFolderMove = (folderId) => {
    setFolderToMove(folderId);
    setShowFolderMoveModal(true);
  };

  const handleFolderMoveModalClose = () => {
    setShowFolderMoveModal(false);
    setFolderToMove(null);
  };

  const handleFolderMoveModalMove = (targetFolderId) => {
    if (folderToMove) {
      moveFolderToFolder(folderToMove, targetFolderId);
    }
    handleFolderMoveModalClose();
  };

  // Token folder move modal handlers
  const handleTokenFolderMove = (folderId) => {
    setTokenFolderToMove(folderId);
    setShowTokenFolderMoveModal(true);
  };

  const handleTokenFolderMoveModalClose = () => {
    setShowTokenFolderMoveModal(false);
    setTokenFolderToMove(null);
  };

  const handleTokenFolderMoveModalMove = (targetFolderId) => {
    if (tokenFolderToMove) {
      moveTokenFolderToFolder(tokenFolderToMove, targetFolderId);
    }
    handleTokenFolderMoveModalClose();
  };

  // Helper: flatten folders in visual order (root, then each parent followed by its children)
  const getFoldersInVisualOrder = (folders) => {
    const ordered = [];
    const topLevel = (folders || []).filter(f => !f.parentId);
    const addRecursive = (folder) => {
      ordered.push(folder);
      (folders || []).filter(f => f.parentId === folder.id).forEach(child => addRecursive(child));
    };
    topLevel.forEach(addRecursive);
    return ordered;
  };

  // Helper function to generate folder options with paths for MoveModal (in app order)
  const getFolderOptionsWithPaths = (folders) => {
    return getFoldersInVisualOrder(folders).map(folder => ({
      ...folder,
      folderPath: getFolderPathNames(folders, folder).join('/')
    }));
  };

  // Helper function to generate token folder options with paths for MoveModal (in app order)
  const getTokenFolderOptionsWithPaths = (folders) => {
    return getFoldersInVisualOrder(folders).map(folder => ({
      ...folder,
      folderPath: getFolderPathNames(folders, folder).join('/')
    }));
  };

  const deletePrimitiveFolder = (id) => {
    const folder = primitiveFolders.find((f) => f.id === id);
    const folderName = folder ? folder.name : 'this folder';

    const doDelete = () => {
      setPrimitiveFolders((prev) => prev.filter((f) => f.id !== id));
      setPrimitives((prev) => prev.filter((item) => item.folderId !== id));
    };

    if (primitiveFolderContainsReferencedColor(id, false)) {
      showConfirmation({
        title: 'Delete Folder?',
        message: `"${folderName}" contains colors that are used as Reference, Background, or Foreground by other colors. Delete folder and all its colors anyway? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: doDelete
      });
    } else {
      doDelete();
    }
  };

  const duplicatePrimitiveFolder = (id) => {
    const original = primitiveFolders.find(folder => folder.id === id);
    if (!original) return;

    // Recursively duplicate folder and all its children
    const duplicateFolderRecursively = (folderId, newParentId = null, isTopLevel = false) => {
      const folder = primitiveFolders.find(f => f.id === folderId);
      if (!folder) return { newFolderId: null, duplicatedColors: [], duplicatedFolders: [] };

      const newFolderId = generateId();
      const duplicated = {
        ...folder,
        id: newFolderId,
        name: isTopLevel ? folder.name + ' copy' : folder.name,
        parentId: newParentId
      };

      // Duplicate all colors in this folder
      const folderColors = primitives.filter(item => item.folderId === folderId);
      const duplicatedColors = folderColors.map(color => ({
        ...color,
        id: generateId(),
        folderId: newFolderId
      }));

      // Find and duplicate all child folders
      const childFolders = primitiveFolders.filter(f => f.parentId === folderId);
      const duplicatedFolders = [duplicated];
      const allDuplicatedColors = [...duplicatedColors];

      childFolders.forEach(childFolder => {
        const childResult = duplicateFolderRecursively(childFolder.id, newFolderId, false);
        if (childResult.newFolderId) {
          duplicatedFolders.push(...childResult.duplicatedFolders);
          allDuplicatedColors.push(...childResult.duplicatedColors);
        }
      });

      return { newFolderId, duplicatedColors: allDuplicatedColors, duplicatedFolders };
    };

    const result = duplicateFolderRecursively(id, original.parentId || null, true);
    if (!result.newFolderId) return;

    // Find the index of the original folder
    const originalIndex = primitiveFolders.findIndex(folder => folder.id === id);

    setPrimitiveFolders(prev => {
      const newArray = [...prev];
      newArray.splice(originalIndex + 1, 0, ...result.duplicatedFolders);
      return newArray;
    });

    setPrimitives(prev => {
      const next = [...prev, ...result.duplicatedColors];
      // Recompute only duplicated colors
      const duplicatedIds = result.duplicatedColors.map(c => c.id);
      recomputeAffectedColors(duplicatedIds[0], { isToken: false, arrays: { primitives: next }, alsoIds: duplicatedIds.slice(1) });
      return next;
    });
    
    // Set the top-level duplicated folder as expanded and editing
    updateFolderUIState(result.newFolderId, { isExpanded: true, isEditing: true }, false);
  };

  const deleteTokenFolder = (id) => {
    const currentThemeId = getCurrentThemeId();
    const currentFolders = tokenFolders[currentThemeId] || [];
    const folder = currentFolders.find((f) => f.id === id);
    const folderName = folder ? folder.name : 'this folder';

    const doDelete = () => {
      setTokenFolders((prev) => {
        const newFolders = { ...prev };
        colorThemes.forEach((theme) => {
          const themeId = theme.id;
          newFolders[themeId] = (newFolders[themeId] || []).filter((f) => f.id !== id);
        });
        return newFolders;
      });
      setTokens((prev) => {
        const newTokens = { ...prev };
        colorThemes.forEach((theme) => {
          const themeId = theme.id;
          newTokens[themeId] = (newTokens[themeId] || []).filter((item) => item.folderId !== id);
        });
        return newTokens;
      });
    };

    if (tokenFolderContainsReferencedColor(id, false)) {
      showConfirmation({
        title: 'Delete Folder?',
        message: `"${folderName}" contains colors that are used as Reference, Background, or Foreground by other colors. Delete folder and all its colors anyway? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: doDelete
      });
    } else {
      doDelete();
    }
  };

  const duplicateTokenFolder = (id) => {
    const currentThemeId = getCurrentThemeId();
    const currentFolders = tokenFolders[currentThemeId] || [];
    const currentTokens = tokens[currentThemeId] || [];
    
    const original = currentFolders.find(folder => folder.id === id);
    if (!original) return;

    // Recursively duplicate folder and all its children
    const duplicateFolderRecursively = (folderId, newParentId = null, themeFolders, themeTokens, isTopLevel = false) => {
      const folder = themeFolders.find(f => f.id === folderId);
      if (!folder) return { newFolderId: null, duplicatedColors: [], duplicatedFolders: [] };

      const newFolderId = generateId();
      const duplicated = {
        ...folder,
        id: newFolderId,
        name: isTopLevel ? folder.name + ' copy' : folder.name,
        parentId: newParentId
      };

      // Duplicate all colors in this folder
      const folderColors = themeTokens.filter(item => item.folderId === folderId);
      const duplicatedColors = folderColors.map(color => ({
        ...color,
        id: generateId(),
        folderId: newFolderId
      }));

      // Find and duplicate all child folders
      const childFolders = themeFolders.filter(f => f.parentId === folderId);
      const duplicatedFolders = [duplicated];
      const allDuplicatedColors = [...duplicatedColors];

      childFolders.forEach(childFolder => {
        const childResult = duplicateFolderRecursively(childFolder.id, newFolderId, themeFolders, themeTokens, false);
        if (childResult.newFolderId) {
          duplicatedFolders.push(...childResult.duplicatedFolders);
          allDuplicatedColors.push(...childResult.duplicatedColors);
        }
      });

      return { newFolderId, duplicatedColors: allDuplicatedColors, duplicatedFolders };
    };

    const result = duplicateFolderRecursively(id, original.parentId || null, currentFolders, currentTokens, true);
    if (!result.newFolderId) return;

    // Find the index of the original folder
    const originalIndex = currentFolders.findIndex(folder => folder.id === id);

    // Add duplicated folder and colors to ALL themes at the same position
    setTokenFolders(prev => {
      const newFolders = { ...prev };
      colorThemes.forEach(theme => {
        // Initialize theme if it doesn't exist
        if (!newFolders[theme.id]) {
          newFolders[theme.id] = [];
        }
        
        // Insert at the same position in all themes
        const themeFolders = [...(newFolders[theme.id] || [])];
        themeFolders.splice(originalIndex + 1, 0, ...result.duplicatedFolders);
        newFolders[theme.id] = themeFolders;
      });
      return newFolders;
    });

    setTokens(prev => {
      const newTokens = { ...prev };
      colorThemes.forEach(theme => {
        // Initialize theme if it doesn't exist
        if (!newTokens[theme.id]) {
          newTokens[theme.id] = [];
        }
        
        // Add duplicated colors at the end for all themes (since they're new)
        const otherThemeDuplicatedColors = result.duplicatedColors.map(color => ({ ...color }));
        newTokens[theme.id] = [...(newTokens[theme.id] || []), ...otherThemeDuplicatedColors];
      });
      // Recompute only duplicated tokens in current theme
      const duplicatedIds = result.duplicatedColors.map(c => c.id);
      recomputeAffectedColors(duplicatedIds[0], { isToken: true, arrays: { tokens: newTokens }, alsoIds: duplicatedIds.slice(1) });
      return newTokens;
    });
    
    // Set the top-level duplicated folder as expanded and editing
    updateFolderUIState(result.newFolderId, { isExpanded: true, isEditing: true }, true);
  };

  // Move folder functions
  const movePrimitiveFolder = (id, direction) => {
    setPrimitiveFolders(prev => {
      const index = prev.findIndex(folder => folder.id === id);
      if (index === -1) {
        console.warn(`Primitive folder with id ${id} not found`);
        return prev;
      }

      const folder = prev[index];
      const parentId = folder.parentId || null;

      // Only reorder among sibling folders (same parentId, including root)
      const siblingIndices = prev
        .map((f, idx) => ({ f, idx }))
        .filter(({ f }) => (f.parentId || null) === parentId)
        .map(({ idx }) => idx);

      if (siblingIndices.length <= 1) {
        return prev;
      }

      const siblingPosition = siblingIndices.indexOf(index);
      const targetPosition = direction === 'up' ? siblingPosition - 1 : siblingPosition + 1;
      if (targetPosition < 0 || targetPosition >= siblingIndices.length) {
        console.warn(
          `Cannot move primitive folder ${direction}: already at ${
            direction === 'up' ? 'top' : 'bottom'
          } of its parent`
        );
        return prev;
      }

      // Rebuild sibling sequence and write back into original array
      const siblingItems = siblingIndices.map(i => prev[i]);
      const newSiblingItems = [...siblingItems];
      [newSiblingItems[siblingPosition], newSiblingItems[targetPosition]] = [
        newSiblingItems[targetPosition],
        newSiblingItems[siblingPosition],
      ];

      const next = [...prev];
      siblingIndices.forEach((idx, i) => {
        next[idx] = newSiblingItems[i];
      });
      return next;
    });
  };

  const moveTokenFolder = (id, direction) => {
    // Move folder across ALL themes to maintain synchronization
    setTokenFolders(prev => {
      const newFolders = { ...prev };

      // Get the current theme's folder order as the reference
      const currentThemeId = getCurrentThemeId();
      const currentFolders = newFolders[currentThemeId] || [];
      const index = currentFolders.findIndex(folder => folder.id === id);

      if (index === -1) {
        console.warn(`Folder with id ${id} not found in current theme`);
        return prev;
      }

      const folder = currentFolders[index];
      const parentId = folder.parentId || null;

      // Only reorder among sibling folders (same parentId, including root)
      const siblingIndices = currentFolders
        .map((f, idx) => ({ f, idx }))
        .filter(({ f }) => (f.parentId || null) === parentId)
        .map(({ idx }) => idx);

      if (siblingIndices.length <= 1) {
        return prev;
      }

      const siblingPosition = siblingIndices.indexOf(index);
      const targetPosition = direction === 'up' ? siblingPosition - 1 : siblingPosition + 1;
      if (targetPosition < 0 || targetPosition >= siblingIndices.length) {
        console.warn(
          `Cannot move folder ${direction}: already at ${
            direction === 'up' ? 'top' : 'bottom'
          } of its parent`
        );
        return prev;
      }

      // Determine new desired order of sibling IDs based on current theme
      const siblingIds = siblingIndices.map(i => currentFolders[i].id);
      const newSiblingIds = [...siblingIds];
      [newSiblingIds[siblingPosition], newSiblingIds[targetPosition]] = [
        newSiblingIds[targetPosition],
        newSiblingIds[siblingPosition],
      ];

      // Apply the same sibling ID order to all themes
      let movedInAnyTheme = false;
      colorThemes.forEach(theme => {
        const themeId = theme.id;
        const themeFolders = [...(newFolders[themeId] || [])];

        // Map of sibling folders by ID for this theme
        const siblingItemsById = new Map(
          themeFolders
            .filter(f => siblingIds.includes(f.id))
            .map(f => [f.id, f])
        );

        if (siblingItemsById.size === 0) {
          return;
        }

        // Existing positions of these siblings in this theme
        const themeSiblingIndices = themeFolders
          .map((f, idx) => ({ f, idx }))
          .filter(({ f }) => siblingIds.includes(f.id))
          .map(({ idx }) => idx);

        if (themeSiblingIndices.length <= 1) {
          return;
        }

        const reorderedSiblingItems = newSiblingIds
          .filter(fid => siblingItemsById.has(fid))
          .map(fid => siblingItemsById.get(fid));

        if (reorderedSiblingItems.length !== themeSiblingIndices.length) {
          return;
        }

        const nextThemeFolders = [...themeFolders];
        themeSiblingIndices.forEach((idx, i) => {
          nextThemeFolders[idx] = reorderedSiblingItems[i];
        });

        newFolders[themeId] = nextThemeFolders;
        movedInAnyTheme = true;
      });
      
      if (!movedInAnyTheme) {
        console.warn(`Folder ${id} was not found in any theme or could not be moved`);
        return prev;
      }
      
      return newFolders;
    });
  };
  
  // Primitives section handlers
  const handleAddPrimitive = () => {
    const defaultColor = parseColor('oklch(0.5 65% 275)', colorSpace);
    setPrimitives(prev => {
      const name = getUniqueColorName(prev, null);
      const newPrimitive = {
        id: generateId(),
        type: 'color',
        name,
        mode: 'static',
        colorValue: 'oklch(0.5 65% 275)',
        hexValue: defaultColor.isValid ? defaultColor.hex : '#34B9FF',
        oklchValue: 'oklch(0.5 65% 275)',
        p3Support: true,
        folderId: null,
        isEditing: true,
        referenceColorId: '',
        lExpression: '',
        cExpression: '',
        hExpression: '',
        aExpression: ''
      };
      const next = [...prev, newPrimitive];
      recomputeAffectedColors(newPrimitive.id, { isToken: false, arrays: { primitives: next } });
      return next;
    });
  };

  const applyPresetConfig = (config) => {
    // Clear all current state before loading preset
    setPrimitives([]);
    setPrimitiveFolders([]);
    setColorThemes([{ id: 'default', name: 'Default' }]);
    colorThemesRef.current = [{ id: 'default', name: 'Default' }];
    setTokens({});
    setTokenFolders({});
    setSelectedTokenTheme('Default');
    setPrimitivesSectionTitle('Primitives');
    setThemesSectionTitle('Semantic');
    setPrimitivePrecomputed(new Map());
    setTokenPrecomputed(new Map());
    
    if (!config.data || !config.version) {
      throw new Error('Invalid configuration file format');
    }
    let { data } = config;
    const presetContrastAlgorithm = resolveContrastAlgorithm(config, data);
    const presetColorSpace = normalizeColorSpace(
      config?.settings?.colorSpace ?? data?.colorSpace
    );
    let presetPrimitivesSectionTitle = null;
    let presetThemesSectionTitle = null;
    if (data && Array.isArray(data.collections)) {
      const collections = data.collections;
      let primitivesCollection = collections.find(
        (c) => c.name === 'Primitives' || c.id === 'primitives' || c.name === 'Palettes'
      );
      let tokensCollection = collections.find(
        (c) => c.name === 'Themes' || c.id === 'themes' || c.id === 'tokens'
      );
      const scoreAsTokens = (c) => {
        const id = String(c?.id || '').toLowerCase();
        const name = String(c?.name || '').toLowerCase();
        const modes = Array.isArray(c?.modes) ? c.modes : [];
        let score = 0;
        if (id.includes('theme') || id.includes('token')) score += 5;
        if (name.includes('theme') || name.includes('semantic') || name.includes('token')) score += 5;
        if (modes.length > 1) score += 3;
        return score;
      };
      const scoreAsPrimitives = (c) => {
        const id = String(c?.id || '').toLowerCase();
        const name = String(c?.name || '').toLowerCase();
        const modes = Array.isArray(c?.modes) ? c.modes : [];
        let score = 0;
        if (id.includes('primitive') || id.includes('palette')) score += 5;
        if (name.includes('primitive') || name.includes('palette')) score += 5;
        if (modes.length === 1) score += 2;
        return score;
      };
      if (!tokensCollection && collections.length > 0) {
        if (collections.length === 1) {
          const only = collections[0];
          const tokenScore = scoreAsTokens(only);
          const primitiveScore = scoreAsPrimitives(only);
          if (tokenScore > primitiveScore && tokenScore >= 5) {
            tokensCollection = only;
          } else if (!primitivesCollection) {
            primitivesCollection = only;
          }
        } else {
          tokensCollection = [...collections].sort((a, b) => scoreAsTokens(b) - scoreAsTokens(a))[0];
        }
      }
      if (!primitivesCollection) {
        if (collections.length === 1) {
          if (!tokensCollection) primitivesCollection = collections[0];
        } else {
          const candidates = collections.filter((c) => c !== tokensCollection);
          primitivesCollection = [...candidates].sort((a, b) => scoreAsPrimitives(b) - scoreAsPrimitives(a))[0] || null;
        }
      }
      if (primitivesCollection && tokensCollection && primitivesCollection === tokensCollection && collections.length > 1) {
        const candidates = collections.filter((c) => c !== tokensCollection);
        primitivesCollection = [...candidates].sort((a, b) => scoreAsPrimitives(b) - scoreAsPrimitives(a))[0] || null;
      }
      
      // Extract section titles from collections
      if (primitivesCollection) {
        presetPrimitivesSectionTitle = primitivesCollection.name;
      }
      if (tokensCollection) {
        presetThemesSectionTitle = tokensCollection.name;
      }
      
      const legacyData = { ...data, contrastAlgorithm: presetContrastAlgorithm };
      if (primitivesCollection && Array.isArray(primitivesCollection.modes) && primitivesCollection.modes.length > 0) {
        const primMode = primitivesCollection.modes[0];
        legacyData.primitives = primMode.colors || [];
        legacyData.primitiveFolders = primMode.folders || [];
      } else {
        // No primitives in preset - use empty arrays (state already cleared above)
        legacyData.primitives = [];
        legacyData.primitiveFolders = [];
      }
      const allThemes = [];
      const tokensByTheme = {};
      const tokenFoldersByTheme = {};
      const processModes = (collection, isThemesCollection = false) => {
        if (!collection || !Array.isArray(collection.modes)) return;
        collection.modes.forEach((mode) => {
          const modeName = mode.name || mode.id || 'Mode';
          // Generate new theme ID (don't try to match existing themes since we cleared state)
          const themeId = mode.id || `theme-${modeName.toLowerCase().replace(/\s+/g, '-')}`;
          allThemes.push({ id: themeId, name: modeName });
          if (isThemesCollection) {
            tokensByTheme[themeId] = mode.colors || [];
            tokenFoldersByTheme[themeId] = mode.folders || [];
          } else {
            // For primitives collection modes, create themes but with empty tokens
            tokensByTheme[themeId] = [];
            tokenFoldersByTheme[themeId] = [];
          }
        });
      };
      if (tokensCollection) {
        processModes(tokensCollection, true);
        const uniqueThemes = [];
        const seenNames = new Set();
        for (const theme of allThemes) {
          if (!seenNames.has(theme.name)) {
            uniqueThemes.push(theme);
            seenNames.add(theme.name);
          }
        }
        legacyData.colorThemes = uniqueThemes;
        legacyData.tokens = tokensByTheme;
        legacyData.tokenFolders = tokenFoldersByTheme;
      } else if (primitivesCollection && !tokensCollection) {
        // Only primitives collection - create default theme with empty tokens (state already cleared)
        legacyData.colorThemes = [{ id: 'default', name: 'Default' }];
        legacyData.tokens = { 'default': [] };
        legacyData.tokenFolders = { 'default': [] };
      } else {
        // No collections in preset - use defaults (state already cleared)
        legacyData.colorThemes = [{ id: 'default', name: 'Default' }];
        legacyData.tokens = { 'default': [] };
        legacyData.tokenFolders = { 'default': [] };
      }
      if (!legacyData.selectedTokenTheme && Array.isArray(legacyData.colorThemes) && legacyData.colorThemes.length > 0) {
        legacyData.selectedTokenTheme = legacyData.colorThemes[0].name;
      }
      data = legacyData;
    }
    if (!Array.isArray(data.primitives)) throw new Error('Configuration file has invalid primitives data');
    if (!Array.isArray(data.primitiveFolders)) data.primitiveFolders = [];
    if (!Array.isArray(data.colorThemes) || data.colorThemes.length === 0) {
      throw new Error('Configuration file has invalid structure. Please ensure at least one mode/theme exists in your collections.');
    }
    if (typeof data.tokens !== 'object' || data.tokens === null) throw new Error('Configuration file has invalid tokens data');
    if (typeof data.tokenFolders !== 'object' || data.tokenFolders === null) {
      throw new Error('Configuration file has invalid token folders data');
    }
    data.contrastAlgorithm = presetContrastAlgorithm;
    const hydrated = hydrateImportedData(data, {
      contrastAlgorithm: presetContrastAlgorithm
    });
    data = hydrated.data;
    if (hydrated.warnings.length > 0) {
      console.warn('Preset hydration warnings:', hydrated.warnings);
    }
    const dataToApply = singleTheme ? normalizeToSingleThemeData(data) : data;
    isBulkLoadRef.current = true;
    shouldExpandFoldersRef.current = true;
    setPrimitives(dataToApply.primitives || []);
    setPrimitiveFolders(dataToApply.primitiveFolders || []);
    setContrastAlgorithm(dataToApply.contrastAlgorithm === 'wcag' ? 'wcag' : 'apca');
    setColorSpace(presetColorSpace);
    const newThemes = dataToApply.colorThemes || [{ id: 'default', name: 'Default' }];
    setColorThemes(newThemes);
    colorThemesRef.current = newThemes;
    setTokens(dataToApply.tokens || {});
    setTokenFolders(dataToApply.tokenFolders || {});
    const targetTheme = dataToApply.selectedTokenTheme || dataToApply.colorThemes[0]?.name || 'Default';
    const themeExists = dataToApply.colorThemes.some((theme) => theme.name === targetTheme);
    setSelectedTokenTheme(themeExists ? targetTheme : dataToApply.colorThemes[0]?.name || 'Default');
    // Apply section titles from preset config, or use defaults if not found
    if (presetPrimitivesSectionTitle) {
      setPrimitivesSectionTitle(presetPrimitivesSectionTitle);
    } else {
      setPrimitivesSectionTitle('Primitives');
    }
    if (presetThemesSectionTitle) {
      setThemesSectionTitle(presetThemesSectionTitle);
    } else {
      setThemesSectionTitle('Semantic');
    }
  };

  const handleLoadPreset = async () => {
    try {
      const presetModule = await import('../../presets/betterLCH - preset.json');
      applyPresetConfig(presetModule.default || presetModule);
    } catch (error) {
      console.error('Preset loading failed:', error);
      alert(`Failed to load preset: ${error.message}`);
    }
  };

  const handleLoadPresetGraphicDesign = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    const loadPreset = async () => {
      try {
        const presetModule = await import('../../presets/betterLCH - Graphic Design.json');
        applyPresetConfig(presetModule.default || presetModule);
        scheduleFigmaPromo('graphic');
      } catch (error) {
        console.error('Preset loading failed:', error);
        alert(`Failed to load preset: ${error.message}`);
      }
    };

    // Always check for saved config first - MUST check localStorage synchronously
    const hasConfig = hasSavedConfig();
    
    if (hasConfig) {
      // Show modal first, preset will load ONLY after confirmation
      isModalOpenRef.current = true;
      setPendingAction(() => loadPreset);
      setShowUnsavedChangesModal(true);
      setShowStartScreenModal(false); // Close start screen modal
      // CRITICAL: Return early - do NOT load preset here
      // The preset will ONLY load when user clicks "Export and continue" or "Continue anyway"
      return;
    }
    
    // Only proceed if no saved config exists
    setShowStartScreenModal(false); // Close start screen modal
    await loadPreset();
  };

  const handleLoadPresetUIDesign = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    const loadPreset = async () => {
      try {
        const presetModule = await import('../../presets/betterLCH - UI Design.json');
        applyPresetConfig(presetModule.default || presetModule);
        scheduleFigmaPromo('ui');
      } catch (error) {
        console.error('Preset loading failed:', error);
        alert(`Failed to load preset: ${error.message}`);
      }
    };

    // Always check for saved config first - MUST check localStorage synchronously
    const hasConfig = hasSavedConfig();
    
    if (hasConfig) {
      // Show modal first, preset will load ONLY after confirmation
      isModalOpenRef.current = true;
      setUnsavedChangesModalVariant('replace');
      setPendingAction(() => loadPreset);
      setShowUnsavedChangesModal(true);
      setShowStartScreenModal(false); // Close start screen modal
      // CRITICAL: Return early - do NOT load preset here
      // The preset will ONLY load when user clicks "Export and continue" or "Continue anyway"
      return;
    }
    
    // Only proceed if no saved config exists
    setShowStartScreenModal(false); // Close start screen modal
    await loadPreset();
  };

  const handleAddPrimitiveFolder = () => {
    const folderId = generateId();
    const name = getUniqueFolderName(primitiveFolders, null, null);
    const newFolder = {
      id: folderId,
      name,
      isExpanded: true,
      isEditing: false,
      parentId: null
    };
    
    setPrimitiveFolders(prev => [...prev, newFolder]);
    
    // Set folder as expanded and editing
    updateFolderUIState(folderId, { isExpanded: true, isEditing: true }, false);
  };

  // Themes section handlers
  const handleAddToken = () => {
    const tokenId = generateId();
    let defaultReferenceColorId = '';
    if (primitives.length > 0) defaultReferenceColorId = primitives[0].id;
    setTokens(prev => {
      const currentTokens = prev[getCurrentThemeId()] || [];
      const name = getUniqueColorName(currentTokens, null);
      const newToken = {
        id: tokenId,
        type: 'color',
        name,
        mode: 'linked',
        colorValue: '#34B9FF',
        hexValue: '#34B9FF',
        oklchValue: 'oklch(0.5 0.25 240)',
        p3Support: true,
        folderId: null,
        isEditing: true,
        referenceColorId: defaultReferenceColorId,
        lExpression: '',
        cExpression: '',
        hExpression: '',
        aExpression: ''
      };
      const newTokens = { ...prev };
      colorThemesRef.current.forEach(theme => {
        if (!newTokens[theme.id]) newTokens[theme.id] = [];
        newTokens[theme.id] = [...newTokens[theme.id], { ...newToken }];
      });
      recomputeAffectedColors(tokenId, { isToken: true, arrays: { tokens: newTokens } });
      return newTokens;
    });
  };

  const handleAddTokenFolder = () => {
    const folderId = generateId();
    setTokenFolders(prev => {
      const currentThemeId = getCurrentThemeId();
      const currentFolders = prev[currentThemeId] || [];
      const name = getUniqueFolderName(currentFolders, null, null);
      const newFolder = {
        id: folderId,
        name,
        isExpanded: true,
        isEditing: false,
        parentId: null
      };
      const newFolders = { ...prev };
      const currentThemes = colorThemesRef.current;
      currentThemes.forEach(theme => {
        if (!newFolders[theme.id]) newFolders[theme.id] = [];
        newFolders[theme.id] = [...newFolders[theme.id], { ...newFolder }];
      });
      return newFolders;
    });
    updateFolderUIState(folderId, { isExpanded: true, isEditing: true }, true);
  };

  // Generate theme options from current themes
  const tokenThemeOptions = colorThemes.map(theme => ({
    value: theme.name,
    label: theme.name
  }));

  // Helper functions to organize content
  const getUnfolderedItems = (items) => items.filter(item => !item.folderId);
  const getFolderedItems = (items, folderId) => items.filter(item => item.folderId === folderId);
  const getTopLevelFolders = (folders) => (folders || []).filter(f => !f.parentId);
  const getChildFolders = (folders, parentId) => (folders || []).filter(f => f.parentId === parentId);
  const getFolderPathNames = (folders, folder) => {
    const names = [];
    let current = folder;
    while (current && current.parentId) {
      const parent = (folders || []).find(f => f.id === current.parentId);
      if (!parent) break;
      names.unshift(parent.name);
      current = parent;
    }
    // Add the folder's own name to complete the full path
    names.push(folder.name);
    return names;
  };

  const getParentFolderNames = (folders, folder) => {
    const names = [];
    let current = folder;
    while (current && current.parentId) {
      const parent = (folders || []).find(f => f.id === current.parentId);
      if (!parent) break;
      names.unshift(parent.name);
      current = parent;
    }
    // Don't add the folder's own name - only parent folders
    return names;
  };

  const createPrimitiveSubfolder = (parentFolder) => {
    const folderId = generateId();
    const name = getUniqueFolderName(primitiveFolders, parentFolder.id, null);
    const newFolder = { id: folderId, name, isExpanded: true, isEditing: false, parentId: parentFolder.id };
    setPrimitiveFolders(prev => [...prev, newFolder]);
    
    // Set folder as expanded and editing
    updateFolderUIState(folderId, { isExpanded: true, isEditing: true }, false);
  };

  const createTokenSubfolderAcrossThemes = (parentFolder) => {
    const folderId = generateId();
    const currentTokenFolders = tokenFolders[getCurrentThemeId()] || [];
    const name = getUniqueFolderName(currentTokenFolders, parentFolder.id, null);
    const newFolder = { id: folderId, name, isExpanded: true, isEditing: false, parentId: parentFolder.id };
    setTokenFolders(prev => {
      const newFolders = { ...prev };
      const currentThemes = colorThemesRef.current; // Always gets latest themes!
      console.log('[createTokenSubfolderAcrossThemes] Adding subfolder to themes:', currentThemes.map(t => t.name).join(', '));
      currentThemes.forEach(theme => {
        if (!newFolders[theme.id]) newFolders[theme.id] = [];
        newFolders[theme.id] = [...newFolders[theme.id], { ...newFolder }];
        console.log(`[createTokenSubfolderAcrossThemes] Added subfolder "${newFolder.name}" to theme "${theme.name}"`);
      });
      return newFolders;
    });
    
    // Set folder as expanded and editing
    updateFolderUIState(folderId, { isExpanded: true, isEditing: true }, true);
  };

  const deletePrimitiveFolderRecursive = (targetId) => {
    const folder = primitiveFolders.find((f) => f.id === targetId);
    const folderName = folder ? folder.name : 'this folder';

    const doDelete = () => {
      const collect = (id) => {
        const children = primitiveFolders.filter((f) => f.parentId === id);
        return [id, ...children.flatMap((ch) => collect(ch.id))];
      };
      const idsToDelete = collect(targetId);
      setPrimitiveFolders((prev) => prev.filter((f) => !idsToDelete.includes(f.id)));
      setPrimitives((prev) => prev.filter((item) => !idsToDelete.includes(item.folderId)));
    };

    if (primitiveFolderContainsReferencedColor(targetId, true)) {
      showConfirmation({
        title: 'Delete Folder?',
        message: `"${folderName}" contains colors that are used as Reference, Background, or Foreground by other colors. Delete folder and all its colors anyway? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: doDelete
      });
    } else {
      doDelete();
    }
  };

  const deleteTokenFolderRecursiveAcrossThemes = (targetId) => {
    const currentThemeId = getCurrentThemeId();
    const currentFolders = tokenFolders[currentThemeId] || [];
    const folder = currentFolders.find((f) => f.id === targetId);
    const folderName = folder ? folder.name : 'this folder';

    const doDelete = () => {
      const collect = (id) => {
        const children = currentFolders.filter((f) => f.parentId === id);
        return [id, ...children.flatMap((ch) => collect(ch.id))];
      };
      const idsToDelete = collect(targetId);
      setTokenFolders((prev) => {
        const newFolders = { ...prev };
        colorThemes.forEach((theme) => {
          newFolders[theme.id] = (newFolders[theme.id] || []).filter((f) => !idsToDelete.includes(f.id));
        });
        return newFolders;
      });
      setTokens((prev) => {
        const newTokens = { ...prev };
        colorThemes.forEach((theme) => {
          newTokens[theme.id] = (newTokens[theme.id] || []).filter((t) => !idsToDelete.includes(t.folderId));
        });
        return newTokens;
      });
    };

    if (tokenFolderContainsReferencedColor(targetId, true)) {
      showConfirmation({
        title: 'Delete Folder',
        message: `"${folderName}" contains colors that are used as Reference, Background, or Foreground by other colors. Delete folder and all its colors anyway? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: doDelete
      });
    } else {
      doDelete();
    }
  };

  const renderPrimitiveFolderTree = (folder) => {
    const folderItems = getFolderedItems(primitives, folder.id);
    const children = getChildFolders(primitiveFolders, folder.id);
    const precomputedColors = primitivePrecomputed;
      return (
      <div key={folder.id} className="main__folder">
        <FolderHeader
          folderName={folder.name}
          isExpanded={getFolderUIState(folder.id, false).isExpanded}
          isEditing={getFolderUIState(folder.id, false).isEditing}
          parentPathNames={getParentFolderNames(primitiveFolders, folder)}
          onToggleExpanded={() => {
            const currentState = getFolderUIState(folder.id, false);
            updateFolderUIState(folder.id, { isExpanded: !currentState.isExpanded }, false);
          }}
          onNameChange={(newName) => {
            const trimmed = (newName || '').trim();
            const name = trimmed === '' ? getUniqueFolderName(primitiveFolders, folder.parentId, folder.id) : trimmed;
            updatePrimitiveFolder(folder.id, { name });
          }}
          onStartEditing={() => updateFolderUIState(folder.id, { isEditing: true }, false)}
          onStopEditing={() => updateFolderUIState(folder.id, { isEditing: false }, false)}
          onAddColor={() => {
            const defaultColor = parseColor('oklch(0.5 65% 275)', colorSpace);
            setPrimitives(prev => {
              const name = getUniqueColorName(prev, null, folder.id);
              const newPrimitive = {
                id: generateId(),
                type: 'color',
                name,
                mode: 'static',
                colorValue: 'oklch(0.5 65% 275)',
                hexValue: defaultColor.isValid ? defaultColor.hex : '#34B9FF',
                oklchValue: 'oklch(0.5 65% 275)',
                p3Support: true,
                folderId: folder.id,
                isEditing: true,
                referenceColorId: '',
                lExpression: '',
                cExpression: '',
                hExpression: '',
                aExpression: ''
              };
              const next = [...prev, newPrimitive];
              recomputeAffectedColors(newPrimitive.id, { isToken: false, arrays: { primitives: next } });
              return next;
            });
          }}
          onAddFolder={() => createPrimitiveSubfolder(folder)}
          onRename={() => updateFolderUIState(folder.id, { isEditing: true }, false)}
          onDuplicate={() => duplicatePrimitiveFolder(folder.id)}
          onMove={() => handleFolderMove(folder.id)}
          onMoveUp={() => movePrimitiveFolder(folder.id, 'up')}
          onMoveDown={() => movePrimitiveFolder(folder.id, 'down')}
          onDelete={() => deletePrimitiveFolderRecursive(folder.id)}
          className="main__folder-header"
        />
        <div className={`main__folder-content ${!getFolderUIState(folder.id, false).isExpanded ? 'main__folder-content--hidden' : ''}`}>
          {folderItems.map((primitive, index) => (
          <CardColor
            key={primitive.id}
            mode={primitive.mode}
            colorName={primitive.name}
            colorValue={primitive.colorValue}
            oklchValue={primitive.oklchValue}
            p3Support={primitive.p3Support}
            isEditing={primitive.isEditing || false}
              availableColors={allAvailablePrimitives}
              excludeColorId={primitive.id}
              precomputedColors={precomputedColors}
              currentColorId={primitive.id}
              primitivesSectionTitle={primitivesSectionTitle}
              themesSectionTitle={themesSectionTitle}
              folderOptions={getFolderOptionsWithPaths(primitiveFolders)}
              currentFolderId={primitive.folderId}
            
            // Linked color props
            referenceColorId={primitive.referenceColorId}
            lExpression={primitive.lExpression}
            cExpression={primitive.cExpression}
            hExpression={primitive.hExpression}
            aExpression={primitive.aExpression}
            
            // Contrast-based color props
            backgroundColorId={primitive.backgroundColorId}
            foregroundColorId={primitive.foregroundColorId}
            contrastLevel={primitive.contrastLevel != null ? primitive.contrastLevel : (contrastAlgorithm === 'wcag' ? 4.5 : 60)}
            useAlpha={primitive.useAlpha}
            contrastAlgorithm={contrastAlgorithm}
            colorSpace={colorSpace}
            
            onModeChange={(newMode) => updatePrimitive(primitive.id, { mode: newMode })}
            onColorNameChange={(newName) => {
              const trimmed = (newName || '').trim();
              const name = trimmed === '' ? getUniqueColorName(primitives, primitive.id, primitive.folderId) : trimmed;
              updatePrimitive(primitive.id, { name });
            }}
            onEditingComplete={() => clearPrimitiveEditing(primitive.id)}
            onColorValueChange={(newValue) => {
              const parsedColor = parseColor(newValue, colorSpace);
              const update = { 
                colorValue: newValue,
                hexValue: parsedColor.isValid ? parsedColor.hex : newValue 
              };
              
              // Update oklchValue if the input is an OKLCH string
              if (newValue.trim().toLowerCase().startsWith('oklch(')) {
                update.oklchValue = newValue;
              } else if (parsedColor.isValid && parsedColor.oklch) {
                // Convert other formats to OKLCH for oklchValue
                update.oklchValue = formatOklch(parsedColor.oklch);
              }
              
              updatePrimitive(primitive.id, update);
            }}
            onReferenceColorChange={(refId) => updatePrimitive(primitive.id, { referenceColorId: refId })}
            onLCHAExpressionChange={(component, value) => {
              const update = {};
              update[`${component}Expression`] = value;
              updatePrimitive(primitive.id, update);
            }}
            onBackgroundColorChange={(bgId) => updatePrimitive(primitive.id, { backgroundColorId: bgId })}
            onForegroundColorChange={(fgId) => updatePrimitive(primitive.id, { foregroundColorId: fgId })}
            onContrastLevelChange={(level) => updatePrimitive(primitive.id, { contrastLevel: level })}
            onContrastAlphaChange={(useAlpha) => updatePrimitive(primitive.id, { useAlpha: useAlpha })}
            onMoreAction={(action, targetFolderId) => {
              switch (action) {
                case 'rename':
                  // Rename is handled within CardColor component
                  break;
                case 'duplicate':
                  duplicatePrimitive(primitive.id);
                  break;
                case 'move': {
                  // Handle move with target folder ID from modal
                  movePrimitiveToFolder(primitive.id, targetFolderId);
                  break;
                }
                case 'move-up':
                  movePrimitive(primitive.id, 'up');
                  break;
                case 'move-down':
                  movePrimitive(primitive.id, 'down');
                  break;
                case 'delete':
                  deletePrimitive(primitive.id);
                  break;
                default:
                  console.log('Unknown action:', action);
              }
            }}
            className={`main__card-color ${
                index === 0 ? 'card-color__content--first-in-folder' : ''
              } ${index === folderItems.length - 1 ? 'card-color__content--last-in-folder' : ''}`}
          />
        ))}
          {children.map(child => renderPrimitiveFolderTree(child))}
        </div>
      </div>
    );
  };

  const renderTokenFolderTree = (folder, currentTokens, currentTokenFolders) => {
    const folderItems = getFolderedItems(currentTokens, folder.id);
    const children = getChildFolders(currentTokenFolders, folder.id);
    // For theme tokens, combine primitive and token precomputed maps so all
    // dropdown swatches share a single source of truth for display colors.
    const precomputedColors = (() => {
      const merged = new Map(tokenPrecomputed);
      for (const [id, value] of primitivePrecomputed.entries()) {
        merged.set(id, value);
      }
      return merged;
    })();
          return (
            <div key={folder.id} className="main__folder">
              <FolderHeader
                folderName={folder.name}
                isExpanded={getFolderUIState(folder.id, true).isExpanded}
                isEditing={getFolderUIState(folder.id, true).isEditing}
          parentPathNames={getParentFolderNames(currentTokenFolders, folder)}
          onToggleExpanded={() => {
            const currentState = getFolderUIState(folder.id, true);
            updateFolderUIState(folder.id, { isExpanded: !currentState.isExpanded }, true);
          }}
          onNameChange={(newName) => {
            const trimmed = (newName || '').trim();
            const currentTokenFolders = tokenFolders[getCurrentThemeId()] || [];
            const name = trimmed === '' ? getUniqueFolderName(currentTokenFolders, folder.parentId, folder.id) : trimmed;
            updateTokenFolder(folder.id, { name });
          }}
          onStartEditing={() => updateFolderUIState(folder.id, { isEditing: true }, true)}
          onStopEditing={() => updateFolderUIState(folder.id, { isEditing: false }, true)}
                onAddColor={() => {
            const tokenId = generateId();
            let defaultReferenceColorId = '';
            if (primitives.length > 0) defaultReferenceColorId = primitives[0].id;
            setTokens(prev => {
              const currentThemeTokens = prev[getCurrentThemeId()] || [];
              const name = getUniqueColorName(currentThemeTokens, null, folder.id);
              const newToken = {
                id: tokenId,
                type: 'color',
                name,
                mode: 'linked',
                colorValue: '#34B9FF',
                hexValue: '#34B9FF',
                oklchValue: 'oklch(0.5 0.25 240)',
                p3Support: true,
                folderId: folder.id,
                isEditing: true,
                referenceColorId: defaultReferenceColorId,
                lExpression: '',
                cExpression: '',
                hExpression: '',
                aExpression: ''
              };
              const newTokens = { ...prev };
              const folderName = folder.name;
              const folderParentId = folder.parentId;
              colorThemesRef.current.forEach(theme => {
                if (!newTokens[theme.id]) newTokens[theme.id] = [];
                const themeFolders = tokenFolders[theme.id] || [];
                const matchingFolder = themeFolders.find(f =>
                  f.name === folderName && f.parentId === folderParentId
                );
                const tokenForTheme = {
                  ...newToken,
                  folderId: matchingFolder ? matchingFolder.id : folder.id
                };
                newTokens[theme.id] = [...newTokens[theme.id], tokenForTheme];
              });
              recomputeAffectedColors(tokenId, { isToken: true, arrays: { tokens: newTokens } });
              return newTokens;
            });
          }}
          onAddFolder={() => createTokenSubfolderAcrossThemes(folder)}
          onRename={() => updateFolderUIState(folder.id, { isEditing: true }, true)}
          onDuplicate={() => duplicateTokenFolder(folder.id)}
          onMove={() => handleTokenFolderMove(folder.id)}
          onMoveUp={() => moveTokenFolder(folder.id, 'up')}
          onMoveDown={() => moveTokenFolder(folder.id, 'down')}
          onDelete={() => deleteTokenFolderRecursiveAcrossThemes(folder.id)}
                className="main__folder-header"
              />
              <div className={`main__folder-content ${!getFolderUIState(folder.id, true).isExpanded ? 'main__folder-content--hidden' : ''}`}>
          {folderItems.map((token, index) => (
                <CardColor
              key={token.id}
              mode={token.mode}
              colorName={token.name}
              colorValue={token.colorValue}
              oklchValue={token.oklchValue}
              p3Support={token.p3Support}
              isToken={true}
              isEditing={token.isEditing || false}
              folderOptions={getTokenFolderOptionsWithPaths(currentTokenFolders)}
              currentFolderId={token.folderId}
                  
                  // Linked color props
              referenceColorId={token.referenceColorId}
              lExpression={token.lExpression}
              cExpression={token.cExpression}
              hExpression={token.hExpression}
              aExpression={token.aExpression}
                  
                  // Contrast-based color props
              backgroundColorId={token.backgroundColorId}
              foregroundColorId={token.foregroundColorId}
              contrastLevel={token.contrastLevel != null ? token.contrastLevel : (contrastAlgorithm === 'wcag' ? 4.5 : 60)}
              useAlpha={token.useAlpha}
              contrastAlgorithm={contrastAlgorithm}
              colorSpace={colorSpace}
              availableColors={allAvailableTokenColors}
            excludeColorId={token.id}
              precomputedColors={precomputedColors}
              currentColorId={token.id}
              primitivesSectionTitle={primitivesSectionTitle}
              themesSectionTitle={themesSectionTitle}
              
              onModeChange={(newMode) => updateToken(token.id, { mode: newMode })}
              onColorNameChange={(newName) => {
              const trimmed = (newName || '').trim();
              const name = trimmed === '' ? getUniqueColorName(currentTokens, token.id, token.folderId) : trimmed;
              updateToken(token.id, { name });
            }}
              onEditingComplete={() => clearTokenEditing(token.id)}
                  onColorValueChange={(newValue) => {
                    const parsedColor = parseColor(newValue, colorSpace);
                    const update = { 
                      colorValue: newValue,
                      hexValue: parsedColor.isValid ? parsedColor.hex : newValue 
                    };
                    
                    // Update oklchValue if the input is an OKLCH string
                    if (newValue.trim().toLowerCase().startsWith('oklch(')) {
                      update.oklchValue = newValue;
                    } else if (parsedColor.isValid && parsedColor.oklch) {
                      // Convert other formats to OKLCH for oklchValue
                      update.oklchValue = formatOklch(parsedColor.oklch);
                    }
                    
                updateToken(token.id, update);
                  }}
              onReferenceColorChange={(refId) => updateToken(token.id, { referenceColorId: refId })}
                  onLCHAExpressionChange={(component, value) => {
                    const update = {};
                    update[`${component}Expression`] = value;
                updateToken(token.id, update);
              }}
              onBackgroundColorChange={(bgId) => updateToken(token.id, { backgroundColorId: bgId })}
              onForegroundColorChange={(fgId) => updateToken(token.id, { foregroundColorId: fgId })}
              onContrastLevelChange={(level) => updateToken(token.id, { contrastLevel: level })}
              onContrastAlphaChange={(useAlpha) => updateToken(token.id, { useAlpha: useAlpha })}
                  onMoreAction={(action, targetFolderId) => {
                    switch (action) {
                      case 'rename':
                        // Rename is handled within CardColor component
                        break;
                      case 'duplicate':
                    duplicateToken(token.id);
                        break;
                case 'move': {
                  // Handle move with target folder ID from modal
                  moveTokenToFolder(token.id, targetFolderId);
                  break;
                }
                      case 'move-up':
                    moveToken(token.id, 'up');
                        break;
                      case 'move-down':
                    moveToken(token.id, 'down');
                        break;
                      case 'delete':
                    deleteToken(token.id);
                        break;
                      default:
                        console.log('Unknown action:', action);
                    }
                  }}
                  className={`main__card-color ${
                    index === 0 ? 'card-color__content--first-in-folder' : ''
              } ${index === folderItems.length - 1 ? 'card-color__content--last-in-folder' : ''}`}
                />
              ))}
          {children.map(child => renderTokenFolderTree(child, currentTokens, currentTokenFolders))}
              </div>
      </div>
    );
  };

  // Helper function to get available colors for Primitives (only other Primitives)
  const getAvailablePrimitiveColors = (currentColorId) => {
    const result = [];
    
    // Add unfoldered primitives first (in their original order)
    const unfolderedPrimitives = primitives.filter(color => !color.folderId && color.id !== currentColorId);
    unfolderedPrimitives.forEach(color => {
      const enrichedColor = { ...color, isToken: false, type: 'primitive' };
      result.push(enrichedColor);
    });
    
    // Add foldered primitives in hierarchical folder order (parent followed by its subfolders)
    const pushFolderColorsRecursive = (folder) => {
      const folderColors = primitives.filter(color => color.folderId === folder.id && color.id !== currentColorId);
      const folderPath = getFolderPathNames(primitiveFolders, folder).join('/');
      folderColors.forEach(color => {
        const enrichedColor = { ...color, isToken: false, type: 'primitive', folderName: folder.name, folderPath: folderPath };
        result.push(enrichedColor);
      });
      const children = getChildFolders(primitiveFolders, folder.id);
      children.forEach(child => pushFolderColorsRecursive(child));
    };

    getTopLevelFolders(primitiveFolders).forEach(folder => {
      pushFolderColorsRecursive(folder);
    });
    
    return result;
  };

  // Helper function to get available colors for Themes (both Primitives and other theme colors)
  const getAvailableTokenColors = (currentColorId) => {
    const result = [];
    const currentThemeId = getCurrentThemeId();
    const currentTokens = tokens[currentThemeId] || [];
    const currentTokenFolders = tokenFolders[currentThemeId] || [];
    
    // Add unfoldered primitives first
    const unfolderedPrimitives = primitives.filter(color => !color.folderId && color.id !== currentColorId);
    unfolderedPrimitives.forEach(color => {
      const enrichedColor = { ...color, isToken: false, type: 'primitive', section: 'primitives' };
      result.push(enrichedColor);
    });
    
    // Add foldered primitives in same hierarchical order as app (top-level then children)
    const pushPrimitiveFolderColorsRecursive = (folder) => {
      const folderColors = primitives.filter(color => color.folderId === folder.id && color.id !== currentColorId);
      const folderPath = getFolderPathNames(primitiveFolders, folder).join('/');
      folderColors.forEach(color => {
        const enrichedColor = { ...color, isToken: false, type: 'primitive', section: 'primitives', folderName: folder.name, folderPath: folderPath };
        result.push(enrichedColor);
      });
      const children = getChildFolders(primitiveFolders, folder.id);
      children.forEach(child => pushPrimitiveFolderColorsRecursive(child));
    };
    getTopLevelFolders(primitiveFolders).forEach(folder => pushPrimitiveFolderColorsRecursive(folder));
    
    // Add unfoldered tokens
    const unfolderedTokens = currentTokens.filter(color => !color.folderId && color.id !== currentColorId);
    unfolderedTokens.forEach(color => {
      const enrichedColor = { ...color, isToken: true, type: 'token', section: 'tokens' };
      result.push(enrichedColor);
    });
    
    // Add foldered tokens in hierarchical folder order (parent followed by its subfolders)
    const pushTokenFolderColorsRecursive = (folder) => {
      const folderColors = currentTokens.filter(color => color.folderId === folder.id && color.id !== currentColorId);
      const folderPath = getFolderPathNames(currentTokenFolders, folder).join('/');
      folderColors.forEach(color => {
        const enrichedColor = { ...color, isToken: true, type: 'token', section: 'tokens', folderName: folder.name, folderPath: folderPath };
        result.push(enrichedColor);
      });
      const children = getChildFolders(currentTokenFolders, folder.id);
      children.forEach(child => pushTokenFolderColorsRecursive(child));
    };

    getTopLevelFolders(currentTokenFolders).forEach(folder => {
      pushTokenFolderColorsRecursive(folder);
    });
    
    return result;
  };

  // Single memoized list per section so theme toggle and unrelated updates don't rebuild N lists (avoids O(n²) and re-renders of all Selects).
  const allAvailablePrimitives = useMemo(
    () => getAvailablePrimitiveColors(null),
    [primitives, primitiveFolders]
  );
  const allAvailableTokenColors = useMemo(
    () => getAvailableTokenColors(null),
    [primitives, primitiveFolders, tokens, tokenFolders, selectedTokenTheme, colorThemes]
  );

  // Primitives mode options (Static, Linked, Contrast-based)
  const primitiveModeOptions = [
    { value: 'static', label: 'Static' },
    { value: 'linked', label: 'Linked' },
    { value: 'contrast', label: 'Contrast-based' }
  ];

  // Themes mode options (only Linked and Contrast-based)
  const tokenModeOptions = [
    { value: 'linked', label: 'Linked' },
    { value: 'contrast', label: 'Contrast-based' }
  ];

  // Filter mode options based on section
  const getModeOptions = (isToken) => {
    return isToken ? tokenModeOptions : primitiveModeOptions;
  };

  // Sample color reference options
  const colorReferenceOptions = [
    { value: 'color1', label: 'Color Name', color: '#006aaf' },
    { value: 'color2', label: 'Primary Blue', color: '#0066cc' },
    { value: 'color3', label: 'Accent Color', color: '#ff6b35' }
  ];

  // Render functions for content sections
  const renderPrimitivesContent = () => {
    const unfolderedPrimitives = getUnfolderedItems(primitives);
    const hasContent = primitives.length > 0 || primitiveFolders.length > 0;
    const precomputedColors = primitivePrecomputed;
    

    if (!hasContent) {
      return (
        <div className="main__empty-state main__empty-state--primitives">
          <div className="main__empty-state-content">
            <h2 className="main__empty-state-title">Start creating your primitives</h2>
            <p className="main__empty-state-description">
              Primitives are base-level color values based on which your themes will be built. E.g. Neutral, Brand, Success, Warning, Error colors and their tints and shades, etc.
            </p>
            <div className="main__empty-state-actions">
              <Button
                type="solid"
                size="medium"
                icon={<Icon name="add" size="medium" />}
                onClick={handleAddPrimitive}
                className="main__empty-state-button"
              >
                Add first color
              </Button>
            </div>
          </div>
        </div>
      );
    }

    const renderPrimitiveItem = (primitive, index) => (
          <CardColor
            key={primitive.id}
            mode={primitive.mode}
            colorName={primitive.name}
            colorValue={primitive.colorValue}
            oklchValue={primitive.oklchValue}
            p3Support={primitive.p3Support}
            isEditing={primitive.isEditing || false}
            folderOptions={getFolderOptionsWithPaths(primitiveFolders)}
            currentFolderId={primitive.folderId}
            
            // Linked color props
            referenceColorId={primitive.referenceColorId}
            lExpression={primitive.lExpression}
            cExpression={primitive.cExpression}
            hExpression={primitive.hExpression}
            aExpression={primitive.aExpression}
            
            // Contrast-based color props
            backgroundColorId={primitive.backgroundColorId}
            foregroundColorId={primitive.foregroundColorId}
            contrastLevel={primitive.contrastLevel != null ? primitive.contrastLevel : (contrastAlgorithm === 'wcag' ? 4.5 : 60)}
            useAlpha={primitive.useAlpha}
            contrastAlgorithm={contrastAlgorithm}
            colorSpace={colorSpace}
            
            availableColors={allAvailablePrimitives}
              excludeColorId={primitive.id}
            precomputedColors={precomputedColors}
            currentColorId={primitive.id}
            primitivesSectionTitle={primitivesSectionTitle}
            themesSectionTitle={themesSectionTitle}
            
            onModeChange={(newMode) => updatePrimitive(primitive.id, { mode: newMode })}
            onColorNameChange={(newName) => {
              const trimmed = (newName || '').trim();
              const name = trimmed === '' ? getUniqueColorName(primitives, primitive.id, primitive.folderId) : trimmed;
              updatePrimitive(primitive.id, { name });
            }}
            onEditingComplete={() => clearPrimitiveEditing(primitive.id)}
            onColorValueChange={(newValue) => {
              const parsedColor = parseColor(newValue, colorSpace);
              const update = { 
                colorValue: newValue,
                hexValue: parsedColor.isValid ? parsedColor.hex : newValue 
              };
              
              // Update oklchValue if the input is an OKLCH string
              if (newValue.trim().toLowerCase().startsWith('oklch(')) {
                update.oklchValue = newValue;
              } else if (parsedColor.isValid && parsedColor.oklch) {
                // Convert other formats to OKLCH for oklchValue
                update.oklchValue = formatOklch(parsedColor.oklch);
              }
              
              updatePrimitive(primitive.id, update);
            }}
            onReferenceColorChange={(refId) => updatePrimitive(primitive.id, { referenceColorId: refId })}
            onLCHAExpressionChange={(component, value) => {
              const update = {};
              update[`${component}Expression`] = value;
              updatePrimitive(primitive.id, update);
            }}
            onBackgroundColorChange={(bgId) => updatePrimitive(primitive.id, { backgroundColorId: bgId })}
            onForegroundColorChange={(fgId) => updatePrimitive(primitive.id, { foregroundColorId: fgId })}
            onContrastLevelChange={(level) => updatePrimitive(primitive.id, { contrastLevel: level })}
            onContrastAlphaChange={(useAlpha) => updatePrimitive(primitive.id, { useAlpha: useAlpha })}
            onMoreAction={(action, targetFolderId) => {
              switch (action) {
                case 'rename':
                  // Rename is handled within CardColor component
                  break;
                case 'duplicate':
                  duplicatePrimitive(primitive.id);
                  break;
                case 'move': {
                  // Handle move with target folder ID from modal
                  movePrimitiveToFolder(primitive.id, targetFolderId);
                  break;
                }
                case 'move-up':
                  movePrimitive(primitive.id, 'up');
                  break;
                case 'move-down':
                  movePrimitive(primitive.id, 'down');
                  break;
                case 'delete':
                  deletePrimitive(primitive.id);
                  break;
                default:
                  console.log('Unknown action:', action);
              }
            }}
            className={`main__card-color ${
              index === 0 ? 'card-color__content--first-unfoldered' : ''
            } ${
              index === unfolderedPrimitives.length - 1 ? 'card-color__content--last-unfoldered' : ''
            }`}
          />
        );
    
    return (
      <div className="main__content-list">
        {/* Render unfoldered colors */}
        {unfolderedPrimitives.map(renderPrimitiveItem)}
        
        {/* Render folders using recursive system */}
        {getTopLevelFolders(primitiveFolders).map(folder => renderPrimitiveFolderTree(folder))}
      </div>
    );
  };

  const renderTokensContent = () => {
    const currentThemeId = getCurrentThemeId();
    const currentTokens = tokens[currentThemeId] || [];
    const currentTokenFolders = tokenFolders[currentThemeId] || [];
    // Tokens reference both primitives and other tokens, so merge the two
    // precomputed maps. Primitive entries from primitivePrecomputed win to
    // ensure they always reflect the latest calculations.
    const precomputedColors = (() => {
      const merged = new Map(tokenPrecomputed);
      for (const [id, value] of primitivePrecomputed.entries()) {
        merged.set(id, value);
      }
      return merged;
    })();
    
    const unfolderedTokens = getUnfolderedItems(currentTokens);
    const hasContent = currentTokens.length > 0 || currentTokenFolders.length > 0;
    const hasPrimitives = primitives.length > 0;

    if (!hasContent) {
      // Show different empty state based on whether primitives exist
      if (!hasPrimitives) {
        return (
          <div className="main__empty-state main__empty-state--themes main__empty-state--themes-no-primitives">
            <p className="main__empty-text">To set up semantic tokens add at least one primitive first.</p>
          </div>
        );
      } else {
        return (
          <div className="main__empty-state main__empty-state--themes">
            <div className="main__empty-state-content">
              <h2 className="main__empty-state-title">Set up your semantic tokens</h2>
              <p className="main__empty-state-description">
                Semantic tokens describe the purpose or function of a color. E.g. Background, Foreground, Text, Icon, Action colors, etc.
              </p>
              <div className="main__empty-state-actions">
                <Button
                  type="outline"
                  size="medium"
                  icon={<Icon name="add" size="medium" />}
                  onClick={handleAddToken}
                  className="main__empty-state-button"
                >
                  Add first color
                </Button>
              </div>
            </div>
          </div>
        );
      }
    }

    const renderTokenItem = (token, index) => (
                <CardColor
                  key={token.id}
                  mode={token.mode}
                  colorName={token.name}
                  colorValue={token.colorValue}
                  oklchValue={token.oklchValue}
                  p3Support={token.p3Support}
                  isToken={true}
                  isEditing={token.isEditing || false}
                  folderOptions={getTokenFolderOptionsWithPaths(currentTokenFolders)}
                  currentFolderId={token.folderId}
                  
                  // Linked color props
                  referenceColorId={token.referenceColorId}
                  lExpression={token.lExpression}
                  cExpression={token.cExpression}
                  hExpression={token.hExpression}
                  aExpression={token.aExpression}
                  
                  // Contrast-based color props
                  backgroundColorId={token.backgroundColorId}
                  foregroundColorId={token.foregroundColorId}
                  contrastLevel={token.contrastLevel != null ? token.contrastLevel : (contrastAlgorithm === 'wcag' ? 4.5 : 60)}
                  useAlpha={token.useAlpha}
                  contrastAlgorithm={contrastAlgorithm}
                  colorSpace={colorSpace}
            
            availableColors={allAvailableTokenColors}
            excludeColorId={token.id}
            precomputedColors={precomputedColors}
                  currentColorId={token.id}
                  primitivesSectionTitle={primitivesSectionTitle}
                  themesSectionTitle={themesSectionTitle}
                  
                  onModeChange={(newMode) => updateToken(token.id, { mode: newMode })}
                  onColorNameChange={(newName) => {
              const trimmed = (newName || '').trim();
              const name = trimmed === '' ? getUniqueColorName(currentTokens, token.id, token.folderId) : trimmed;
              updateToken(token.id, { name });
            }}
                  onColorValueChange={(newValue) => {
                    const parsedColor = parseColor(newValue, colorSpace);
                    const update = { 
                      colorValue: newValue,
                      hexValue: parsedColor.isValid ? parsedColor.hex : newValue 
                    };
                    
                    // Update oklchValue if the input is an OKLCH string
                    if (newValue.trim().toLowerCase().startsWith('oklch(')) {
                      update.oklchValue = newValue;
                    } else if (parsedColor.isValid && parsedColor.oklch) {
                      // Convert other formats to OKLCH for oklchValue
                      update.oklchValue = formatOklch(parsedColor.oklch);
                    }
                    
                    updateToken(token.id, update);
                  }}
                  onReferenceColorChange={(refId) => updateToken(token.id, { referenceColorId: refId })}
                  onLCHAExpressionChange={(component, value) => {
                    const update = {};
                    update[`${component}Expression`] = value;
                    updateToken(token.id, update);
                  }}
                  onBackgroundColorChange={(bgId) => updateToken(token.id, { backgroundColorId: bgId })}
                  onForegroundColorChange={(fgId) => updateToken(token.id, { foregroundColorId: fgId })}
                  onContrastLevelChange={(level) => updateToken(token.id, { contrastLevel: level })}
                  onContrastAlphaChange={(useAlpha) => updateToken(token.id, { useAlpha: useAlpha })}
                  onMoreAction={(action, targetFolderId) => {
                    switch (action) {
                      case 'rename':
                        // Rename is handled within CardColor component
                        break;
                      case 'duplicate':
                        duplicateToken(token.id);
                        break;
                case 'move': {
                  // Handle move with target folder ID from modal
                  moveTokenToFolder(token.id, targetFolderId);
                  break;
                }
                      case 'move-up':
                        moveToken(token.id, 'up');
                        break;
                      case 'move-down':
                        moveToken(token.id, 'down');
                        break;
                      case 'delete':
                        deleteToken(token.id);
                        break;
                      default:
                        console.log('Unknown action:', action);
                    }
                  }}
                  className={`main__card-color ${
              index === 0 ? 'card-color__content--first-unfoldered' : ''
            } ${
              index === unfolderedTokens.length - 1 ? 'card-color__content--last-unfoldered' : ''
                  }`}
                />
              );
    
    return (
      <div className="main__content-list">
        {/* Render unfoldered colors */}
        {unfolderedTokens.map(renderTokenItem)}
        
        {/* Render folders using recursive system */}
        {getTopLevelFolders(currentTokenFolders).map(folder => renderTokenFolderTree(folder, currentTokens, currentTokenFolders))}
      </div>
    );
  };

  return (
    <div className="main">
      <SEOProtection noIndex={true} />
      <AppHeader
        onImportConfigs={handleImportConfigs}
        onExport={handleExport}
        hideImportExport={showStartScreen}
        logoHref="/"
        onNewClick={() => setShowStartScreenModal(!showStartScreenModal)}
        showNewButton={!showStartScreen}
        isNewModalOpen={showStartScreenModal}
        contrastAlgorithm={contrastAlgorithm}
        onContrastAlgorithmToggle={handleContrastAlgorithmToggle}
        colorSpace={colorSpace}
        onColorSpaceToggle={handleColorSpaceToggle}
        className="main__header"
      />
      
      <main className={`main__content ${showStartScreenModal ? 'main__content--modal-open' : ''}`}>
        {showStartScreen ? (
          <div className="main__start-screen">
            <div className="main__start-screen-cards">
              <button
                type="button"
                className="main__start-screen-card"
                onClick={handleStartFromScratch}
                aria-label="Start from scratch"
              >
                <span className="main__start-screen-card-cover">
                  <span className="main__start-screen-card-cover-icon">
                    <Icon name="add" size="medium" />
                  </span>
                </span>
                <span className="main__start-screen-card-content">Start from scratch</span>
              </button>
              <button
                type="button"
                className="main__start-screen-card"
                onClick={handleImportConfigs}
                aria-label="Import config"
              >
                <span className="main__start-screen-card-cover">
                  <span className="main__start-screen-card-cover-icon">
                    <Icon name="upload" size="medium" />
                  </span>
                </span>
                <span className="main__start-screen-card-content">Import config</span>
              </button>
              <button
                type="button"
                className="main__start-screen-card"
                onClick={handleLoadPresetGraphicDesign}
                aria-label="Try Graphic Design preset"
              >
                <span className="main__start-screen-card-cover main__start-screen-card-cover--image main__start-screen-card-cover--graphic" aria-hidden />
                <span className="main__start-screen-card-content">Try Graphic Design preset</span>
              </button>
              <button
                type="button"
                className="main__start-screen-card"
                onClick={handleLoadPresetUIDesign}
                aria-label="Try UI Design preset"
              >
                <span className="main__start-screen-card-cover main__start-screen-card-cover--image main__start-screen-card-cover--ui" aria-hidden />
                <span className="main__start-screen-card-content">Try UI Design preset</span>
              </button>
            </div>
          </div>
        ) : (
        <div className="main__columns">
          {/* Primitives Section */}
          <div className="main__column main__column--primitives">
            <div className="main__section-scroll">
              <SectionHeader
                title={primitivesSectionTitle}
                onTitleChange={(newName) => {
                  const trimmed = (newName || '').trim();
                  setPrimitivesSectionTitle(trimmed === '' ? getDefaultSectionTitle(themesSectionTitle) : trimmed);
                }}
                showThemeSelect={false}
                onAddColor={handleAddPrimitive}
                onAddFolder={handleAddPrimitiveFolder}
                className="main__section-header"
              />
              <div className="main__section-content">
                {renderPrimitivesContent()}
              </div>
            </div>
          </div>

          {/* Themes Section */}
          <div className="main__column main__column--tokens">
            <div className="main__section-scroll">
              <SectionHeader
                title={themesSectionTitle}
                onTitleChange={(newName) => {
                  const trimmed = (newName || '').trim();
                  setThemesSectionTitle(trimmed === '' ? getDefaultSectionTitle(primitivesSectionTitle) : trimmed);
                }}
                showThemeSelect={!singleTheme}
                themeSelectStaticLabel={singleTheme ? 'Themes support planned' : undefined}
                selectedTheme={selectedTokenTheme}
                themeOptions={tokenThemeOptions}
                onThemeChange={(option) => setSelectedTokenTheme(option.value)}
                showThemeActionsInDropdown={!singleTheme}
            onThemeManage={{
              buttonRef: themeManageButtonRef,
              currentTheme: getCurrentTheme(),
              onCreateTheme: createTheme,
              onRenameTheme: renameTheme,
              onDeleteTheme: deleteTheme
            }}
                onAddColor={primitives.length > 0 ? handleAddToken : null}
                onAddFolder={primitives.length > 0 ? handleAddTokenFolder : null}
                className="main__section-header"
              />
              <div className="main__section-content">
                {renderTokensContent()}
              </div>
            </div>
          </div>
        </div>
        )}
      </main>
        
        {/* Theme Management Dropdown now rendered inside SectionHeader to limit re-render scope */}
        
        {/* Primitive Folder Move Modal */}
        <MoveModal
          isOpen={showFolderMoveModal}
          onClose={handleFolderMoveModalClose}
          onMove={handleFolderMoveModalMove}
          folders={getFolderOptionsWithPaths(primitiveFolders)}
          currentFolderId={folderToMove}
          itemType="folder"
          itemName={folderToMove ? primitiveFolders.find(f => f.id === folderToMove)?.name || 'Unknown' : ''}
        />

        {/* Token Folder Move Modal */}
        <MoveModal
          isOpen={showTokenFolderMoveModal}
          onClose={handleTokenFolderMoveModalClose}
          onMove={handleTokenFolderMoveModalMove}
          folders={getTokenFolderOptionsWithPaths(tokenFolders[getCurrentThemeId()] || [])}
          currentFolderId={tokenFolderToMove}
          itemType="folder"
          itemName={tokenFolderToMove ? (tokenFolders[getCurrentThemeId()] || []).find(f => f.id === tokenFolderToMove)?.name || 'Unknown' : ''}
        />

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={handleConfirmationModalClose}
          onConfirm={handleConfirmationModalConfirm}
          title={confirmationConfig.title}
          message={confirmationConfig.message}
          confirmText={confirmationConfig.confirmText}
          cancelText={confirmationConfig.cancelText}
          type={confirmationConfig.type}
        />
        <ConfirmationModal
          isOpen={showUnsavedChangesModal}
          onClose={() => {
            isModalOpenRef.current = false;
            setShowUnsavedChangesModal(false);
            setUnsavedChangesModalVariant(null);
            setPendingAction(null);
          }}
          onConfirm={handleUnsavedChangesContinueAnyway}
          onSecondaryConfirm={handleUnsavedChangesExportAndContinue}
          title={unsavedChangesModalVariant === 'import' ? 'Import new collections?' : 'Continue without saving?'}
          message={unsavedChangesModalVariant === 'import' ? 'This will add or update collections from the file, which may replace your current data for those collections. Export first if you want a backup.' : 'Your current work will be lost. You can export it now to save your progress.'}
          confirmText={unsavedChangesModalVariant === 'import' ? 'Continue' : 'Continue anyway'}
          secondaryConfirmText="Export and continue"
          cancelText="Cancel"
          type="warning"
          secondaryType="solid"
          hideCancel={true}
        />

        {/* Figma Promo Card - shown after loading a preset */}
        {showFigmaPromo && activePresetType && (
          <FigmaPromoCard
            presetType={activePresetType}
            title={activePresetType === 'ui'
              ? 'Figma Playground for UI Design'
              : 'Figma Playground for Graphic Design'
            }
            onClose={handleFigmaPromoClose}
            onDontShowAgain={handleFigmaPromoDontShowAgain}
          />
        )}
        <StartScreenModal
          isOpen={showStartScreenModal}
          onClose={() => setShowStartScreenModal(false)}
          onStartFromScratch={handleStartFromScratch}
          onImportConfigs={handleImportConfigs}
          onLoadPresetGraphicDesign={handleLoadPresetGraphicDesign}
          onLoadPresetUIDesign={handleLoadPresetUIDesign}
        />
      </div>
    );
  };
